import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type Cell,
  type Column,
  type ColumnDef,
  type ColumnOrderState,
  type ColumnSizingState,
  type Header,
  type Row,
  type RowData,
  type SortingState,
  type VisibilityState,
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  ChevronDown,
  Columns3,
  Eye,
  EyeOff,
  RotateCcw,
  RotateCw,
} from 'lucide-react';
import {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
  type RefObject,
  type UIEvent as ReactUIEvent,
} from 'react';

import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { EmptyState } from '@/components/ui/EmptyState';
import { EndOfList } from '@/components/ui/EndOfList';
import { deriveError, ErrorState } from '@/components/ui/ErrorState';
import { Menu, type MenuItem } from '@/components/ui/Menu';
import { Popover, PopoverFooter, PopoverHeader } from '@/components/ui/Popover';
import { cn } from '@/lib/cn';
import { SORT_LOCK_REASON, SORT_LOCK_TAG } from '@/lib/sortLock';
import type { ID, SortSpec } from '@shared/types';

/**
 * DataTable — the grid the whole product is built on.
 *
 * TanStack Table v8 for the column model, @tanstack/react-virtual for the rows, and a
 * flex + absolute layout rather than a <table>, because sticky columns, live resizing and
 * virtualisation cannot all coexist inside table layout. ARIA is therefore supplied by
 * hand: role=grid, row/column indices, aria-sort, aria-selected, roving tabindex.
 *
 * Performance contract: 900 rows scroll at 60 fps. Rows and cells are memoised and take
 * only primitives and stable callbacks; hover and the action rail are pure CSS so nothing
 * re-renders on pointer move; skeleton rows are exactly one real row tall so the layout
 * never shifts when data lands.
 *
 * Keyboard: j/k or ↑/↓ move the focused row, Shift+j/k extends the selection, x toggles,
 * Enter opens, Space previews, Home/End/PageUp/PageDown jump.
 */

/* ── public types ─────────────────────────────────────────────────────────── */

declare module '@tanstack/react-table' {
  interface ColumnMeta<TData extends RowData, TValue> {
    /** Cell + header alignment. Numeric columns should be `right`. */
    align?: 'left' | 'right' | 'center';
    /** Human name for the column picker and the header menu. */
    label?: string;
    /** Field sent to the API when this column is sorted. Defaults to the column id. */
    sortField?: string;
    /**
     * What kind of value the column holds, which is what decides the direction a
     * *first* click on its header sorts in. Dates and numbers open descending —
     * newest and highest first — and text opens ascending, which is the
     * convention Linear, Attio and Stripe all hold. Without it every column
     * opened `asc` and the first click on a lead-score column put the worst lead
     * on top. Defaults to `text`.
     */
    sortKind?: 'date' | 'number' | 'text';
    /** Default true. */
    hideable?: boolean;
    /** Default true. */
    resizable?: boolean;
    cellClassName?: string;
    headerClassName?: string;
    /** Opts the cell out of the default single-line truncation. */
    noTruncate?: boolean;
    /**
     * How much of any surplus width this column takes. `true` is weight 1; a
     * number sets the share, so `2` grows twice as fast as `1`. Without any of
     * these a table designed for 1440 leaves the difference as dead gutter at
     * 1920 while its text columns keep truncating.
     */
    flex?: boolean | number;
    /**
     * The leading glyph the loading skeleton must stand in for — otherwise the
     * avatar or monogram pops in from nothing when the data lands.
     */
    skeletonLead?: 'avatar' | 'avatar-xs' | 'monogram';
    /**
     * The SHAPE the skeleton draws for this column's value. Default is the 8px
     * text bar every cell gets; `chip` draws the bordered `--h-control-sm` box a
     * record-reference cell actually lands as, so 31 outlined rectangles do not
     * pop into existence at the moment the data arrives.
     */
    skeletonShape?: 'text' | 'chip';
  }
}

export type DataTableColumn<T> = ColumnDef<T, unknown>;
export type DataTableDensity = 'comfortable' | 'compact';

export interface DataTableColumnState {
  order: string[];
  visibility: Record<string, boolean>;
  sizing: Record<string, number>;
}

export interface DataTableColumnStateProp extends Partial<DataTableColumnState> {
  onChange: (next: DataTableColumnState) => void;
}

export interface DataTableSelectionProp {
  selected: ReadonlySet<ID>;
  onChange: (next: Set<ID>) => void;
  /** Shift+click and Shift+j/k select a contiguous range. Default true. */
  enableRange?: boolean;
  /** True once the user escalated from "these rows" to "every matching row". */
  allMatching?: boolean;
  /** Provide to offer the escalation banner; omit to disable escalation. */
  onSelectAllMatching?: (next: boolean) => void;
}

export interface DataTableSortingProp {
  value: SortSpec[];
  onChange: (next: SortSpec[]) => void;
}

export interface DataTableProps<T> {
  data: T[];
  columns: DataTableColumn<T>[];
  getRowId: (row: T) => ID;

  /** Total rows matching the current filters. Drives aria-rowcount and the escalation. */
  rowCount?: number;

  selection?: DataTableSelectionProp;
  /** Omit for client-side sorting of `data`; provide for server-side sorting. */
  sorting?: DataTableSortingProp;
  columnState?: DataTableColumnStateProp;

  onRowClick?: (row: T) => void;
  /** Enter or double-click. Falls back to `onRowClick`. */
  onRowOpen?: (row: T) => void;
  /** Space. */
  onRowPreview?: (row: T) => void;
  activeRowId?: ID | null;

  loading?: boolean;
  loadingRows?: number;
  /** Rendered when there is no data and no filter is active. */
  empty?: ReactNode;
  /** Rendered when there is no data but a query or filters are active. */
  filteredEmpty?: ReactNode;
  isFiltered?: boolean;
  error?: unknown;
  onRetry?: () => void;
  /** Replaces the built-in error surface. */
  errorState?: ReactNode;

  onLoadMore?: () => void;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  /**
   * A bar is floating over the bottom of the grid (a bulk-action strip). The
   * scroll container reserves its height so the bar docks in space of its own
   * instead of covering the last two rows. Set as a data attribute rather than a
   * conditional class so the virtualiser's measured height never changes.
   */
  floatingBarOpen?: boolean;
  /**
   * Noun for the end-of-list marker: "End of list — 912 inquiries". Defaults to
   * the grid's own label, lower-cased, with a naive singular.
   *
   * Both halves, because the marker agrees with its number: a one-row result
   * ends "End of list — 1 inquiry", never "1 inquiries".
   */
  rowNoun?: { one: string; many: string };

  density?: DataTableDensity;
  /** Freezes the first data column beside the selection gutter. */
  stickyFirstColumn?: boolean;

  /**
   * Default true. Set false when the screen already exposes column visibility
   * from its own display menu — two settings controls in the same gutter doing
   * overlapping jobs is one too many.
   */
  showColumnPicker?: boolean;

  /**
   * A sticky summary rail under the rows: a note on the left, plus optional
   * per-column figures keyed by column id so a total sits under its own column.
   */
  footer?: { note?: ReactNode; cells?: Record<string, ReactNode> };

  /** Right-aligned controls revealed on row hover. Never shifts the row's content. */
  rowActions?: (row: T) => ReactNode;
  /**
   * What the trailing action cell is CALLED — "Actions for Ben Sorenson".
   *
   * The rail is the only write affordance on some grids and it is reached by
   * arrowing right onto this cell, so the cell owes a name: without one the last
   * stop on every row was an empty, unnamed gridcell that happened to open a
   * menu on Enter. Defaults to "Row actions".
   */
  rowActionsLabel?: (row: T) => string;
  /**
   * What a ROW is called — "AF-1906", "Ben Sorenson".
   *
   * The selection checkbox is the one control on the row that used to be named
   * positionally ("Select row 1" … "Select row 32"). In a 766-row grid an
   * ordinal identifies nothing, it changes meaning the instant the sort does,
   * and the same row already exposes three correctly-named controls beside it
   * ("Copy AF-1906", "Stage: New. Change stage", "Actions for AF-1906"). Give
   * the grid the row's own identity and the box says "Select AF-1906".
   *
   * Optional, and the ordinal remains the fallback for a grid whose rows have
   * no short human name.
   */
  rowLabel?: (row: T) => string;
  /** Leading gutter content — an avatar, a status dot. Defaults to the row number. */
  renderRowLead?: (row: T, index: number) => ReactNode;
  /**
   * Print the ordinal in the leading gutter. Default true.
   *
   * Set false where the list is not *ranked*: a position inside an
   * infinite-scroll page of a 913-row list renumbers on every sort and filter
   * change and is not a fact the reader can act on — Linear, Attio and Stripe
   * all leave it out. The 30px slot stays reserved either way, so the hover
   * checkbox never shifts the row. Ignored when `renderRowLead` is supplied.
   */
  showRowIndex?: boolean;

  /**
   * One rung of the grid's Escape ladder, between "step out of the cell" and
   * "leave the region". Return `true` to consume the key — the owner cleared a
   * selection, say — and focus stays on the row that had it. Return `false` (or
   * omit the prop) and Escape bubbles to the screen as before.
   */
  onEscape?: () => boolean;

  className?: string;
  /**
   * DOM id for the `role=grid` scroll container. Set it when something outside
   * the table has to address the list directly — the shell's "Skip to content"
   * link points at it so stop 0 lands on the screen's primary object.
   */
  id?: string;
  /** Accessible name for the grid. */
  label: string;
}

/* ── metrics ──────────────────────────────────────────────────────────────── */

/** Fallbacks mirror --h-row / --h-row-compact; used only if the token cannot be read. */
const ROW_HEIGHT_FALLBACK: Record<DataTableDensity, number> = { comfortable: 36, compact: 32 };
const OVERSCAN = 12;
/** Both gutters are one control tall and wide, so the grid lines up with the shell. */
const GUTTER = 'var(--h-control)';

function readPxVar(name: string, fallback: number): number {
  if (typeof window === 'undefined') return fallback;
  const raw = getComputedStyle(document.documentElement).getPropertyValue(name);
  const parsed = Number.parseFloat(raw);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}

const GRID_CSS = `
/* No transition here, deliberately. --row-bg feeds the 'background' shorthand
   and every non-idle state resolves to a *gradient list*, so the property that
   actually changes is 'background-image', which is not animatable; the
   'transition: background-color' that used to sit here animated a value that
   never moved (rgb(8,21,39) hovered and unhovered alike) and read as a declared
   140ms ease that never ran. The alternative — an ::after wash whose opacity
   transitions — cannot work on this grid: the sticky first column, the sticky
   ordinal gutter and the hover rail all paint --row-bg from their own stacking
   context above the row, so an overlay either misses them or washes over their
   text. The tint step is a state change, and it lands on the frame. */
.af-dt-row { --row-bg: var(--surface-1); background: var(--row-bg);
  /* Stated, not merely absent, and NAMED rather than 'none'.
     'transition: none' computes to an empty property list, which is correct
     today and is a trap tomorrow: these rows are positioned with
     'transform: translateY(...)' and recycled by the virtualiser on every scroll
     frame, so the first person who wants a hover tint to fade will reach for a
     duration on this rule and catch 'transform' with it — every row in the
     viewport animating its own position, i.e. the list smearing while it
     scrolls. An explicit list is a fence: transform, and anything else the
     virtualiser owns, can never be picked up here.
     Nothing actually animates yet. --row-bg feeds the 'background' shorthand and
     every non-idle state resolves to a *gradient list*, so the property that
     moves is 'background-image' and 'background-color' holds --surface-1 in all
     four states — see the comment above for why an ::after wash cannot replace
     it on this grid. The declaration below is therefore inert by measurement and
     safe by construction. */
  transition-property: background-color, box-shadow;
  transition-duration: var(--dur-fast);
  transition-timing-function: var(--ease-out); }
/* Two passes of --surface-hover, not one: a single wash over the row ground is
   a 1.09:1 step, which is not a trackable guide across eight columns. ONE rule
   for both themes — the light theme's wash used to be a '[data-theme='light']'
   override right here carrying its own 5.5% mix, i.e. one theme's row hover came
   from the token layer and the other from this stylesheet. The 5.5% now lives in
   tokens.css as the light --surface-hover, where every other surface can reuse
   and audit it; the two grounds measure 1.23:1 (dark) and 1.25:1 (light) against
   the resting row, and scripts/contrast-check.mjs gates both. */
.af-dt-row:hover { --row-bg: linear-gradient(var(--surface-hover), var(--surface-hover)),
  linear-gradient(var(--surface-hover), var(--surface-hover)), var(--surface-1); }
.af-dt-row[data-selected='true'] { --row-bg: linear-gradient(var(--surface-selected), var(--surface-selected)), var(--surface-1); }
.af-dt-row[data-active='true'] { --row-bg: linear-gradient(var(--surface-active), var(--surface-active)),
  linear-gradient(var(--surface-hover), var(--surface-hover)), var(--surface-1); }
/* The ring is drawn by an overlay, not by the row's own outline. An outline
   belongs to the element that owns it and paints under every *positioned*
   descendant, and this row has three of those: the sticky lead gutter, the
   sticky first column and the hover rail — all of them opaque, all of them
   flush to an edge the ring runs along. The rail alone closed the right-hand
   2px of the focus ring on every focused row. "inset: 0" matches the row box
   exactly, so the ring lands where the outline did, one layer higher. */
.af-dt-row:focus-visible { outline: none; }
.af-dt-row:focus-visible::after { content: ''; position: absolute; inset: 0; z-index: 2;
  pointer-events: none; outline: 2px solid var(--focus); outline-offset: -2px; }
/* The scroll container is the grid's single tab stop. It hands focus straight to
   a row (see its focus handler), but on an empty or errored grid there is no row
   to hand it to — and focus must never be invisible for a keystroke. */
.af-dt-scroll:focus-visible { outline: 2px solid var(--focus); outline-offset: -2px; }
/* ←/→ move focus into the cells, so a focused cell needs the same visible ring
   the focused row has. */
.af-dt-row [role='gridcell']:focus-visible { outline: 2px solid var(--focus);
  outline-offset: -2px; border-radius: 0; }
/* No fill of its own. A --surface-2 band measured 1.058:1 against the rows in
   dark and 1.046:1 in light — under the perceptual floor, so it read as no
   separation at all while also costing the toolbar its boundary. The band sits
   on the row ground and is closed by its own hairline; the toolbar above it
   carries a second one. Two rules, each doing a job. */
.af-dt-head { --row-bg: var(--surface-1); background: var(--row-bg); }
/* THE column head, for every grid in the product — one definition, still the
   shared '.eyebrow' element, re-cut for the one place an eyebrow has to be
   cheap. An eyebrow is a label that sits ALONE and wants to be noticed; a column
   head is a label that sits over 183 rows of data and wants to be read once and
   then ignored. The utility's mono/uppercase/0.14em cut spends about 45% more
   width per label than the same words in the UI face, and on a grid that width
   comes out of the DATA: at 1440 the four numeric heads on /companies claimed
   502px (41.6% of the grid) to display values 7–30px wide, while Segment and
   Size sat hidden for want of 124 and 96. "WON / DECIDED" alone measured 107px
   to head a 22px cell.
   So: the product's UI face at 11px/500, sentence case, no tracking — which is
   what Attio, Linear and Stripe all set a column head at, and which takes
   "Won / decided" to ~72px and "Inquiries" to ~52px. Colour stays the utility's
   '--text-faint'; the head is still the quietest band on the grid. Scoped to
   the head cell, so every eyebrow elsewhere in the product is untouched. */
.af-dt-hcell .eyebrow { font-family: var(--font-sans); font-size: var(--text-xs);
  font-weight: 500; letter-spacing: normal; text-transform: none; }
.af-dt-sticky { background: var(--row-bg); }
.af-dt-sticky-edge { transition: box-shadow var(--dur-normal) var(--ease-out); }
.af-dt-scroll[data-scrolled-x='true'] .af-dt-sticky-edge {
  box-shadow: var(--shadow-2);
  clip-path: inset(0 -12px 0 0);
}
.af-dt-rail { background: var(--row-bg); }
/* Only while the grid is scrolled sideways. The rail is now exactly as wide as
   the gutter it sits in, so at rest it stands beside the last column rather than
   over it, and a ramp would only wash that column's own right edge — which is
   where a right-aligned value ends. Once the grid scrolls, the rail pins to the
   viewport edge and does slide over the columns; without a ramp its hard edge
   cuts a date in half ("Feb 1|7, 2025"), so 24px of fade hands the row over. */
.af-dt-scroll[data-scrolled-x='true'] .af-dt-rail::before { content: ''; position: absolute;
  right: 100%; top: 0; bottom: 0;
  width: var(--space-6); pointer-events: none;
  background: linear-gradient(to right, transparent, var(--row-bg)); }
/* The list ends on a marker and 24px of air, never flush against the window
   edge — "the end" and "the fetch stalled" must not look the same. The inline
   inset is set from JS instead, because it is only affordable when the columns
   are not already at their minimum. */
.af-dt-scroll { padding-bottom: var(--space-6); }
/* A floating bulk bar covers live rows unless the scroller gives up the band it
   docks in. Padding does NOT do that: it sits at the end of the scrolled
   CONTENT, so it only ever clears the bar at the one scroll offset (the very
   bottom) where the bar was never over a row in the first place. At
   scrollTop = 0 the pill still painted over rows 17 and 18.
   The band has to come off the scroll VIEWPORT, and the viewport is a flex item
   of the grid's column with 'flex: 1 1 0%' — so 'block-size' is ignored (the
   used main size comes from the flex basis, not from the height) and 'margin'
   is the property that works: a margin on a flex item is subtracted from the
   free space before it is distributed, so the scroller's box genuinely loses
   the band and the browser does the arithmetic. Nothing inside re-measures;
   the virtualiser sees a shorter viewport, which is exactly the truth.
   The band is the 42px pill plus its 16px offset plus the list's own 24px of
   end-of-list air, spelled from the tokens those are built from. */
.af-dt-scroll[data-bulk-open='true'] { margin-block-end: calc(var(--h-control) + var(--space-4) + var(--space-6)); }
/* The empty state is a composed state, not a layout that ran out of rows. It
   used to be 'items-start' directly under the 32px head, so on a 754px list
   region the block's centre landed at y≈309 against the region's centre of 521
   — 81px of air above it and 505px below, a 1:6 split that reads as a page that
   failed to finish. It fills the region the rows would have filled (the scroll
   port). It is the full port, not the port less a 32px head, because the head
   is not drawn on this state: a column header with no rows under it is a legend
   for nothing.

   ANCHORED TO THE TOP OF THAT REGION, not centred in it. Centring put the
   headline at y≈431 in a 900px viewport — ~300px of dead space above it and
   ~350px below — so the one sentence on the screen sat in the middle of an
   empty page instead of where the first row would have been. --space-16 puts it
   ~64px under the toolbar, in the band the reader is already looking at, which
   is where Linear, Attio and Notion put theirs. */
.af-dt-empty { min-block-size: 100%;
  padding-block-start: var(--space-16); }
/* The narrowed-empty variant keeps the column head above it (see the header rule
   below), so its own offset is measured from the head's bottom edge rather than
   from the toolbar — otherwise the block sits one row-height lower than it does
   on the truly-empty state and the two read as different screens. */
.af-dt-empty[data-under-head='true'] { min-block-size: calc(100% - var(--h-row-compact));
  padding-block-start: calc(var(--space-16) - var(--h-row-compact)); }
.af-dt-foot { background: var(--surface-2); }
.af-dt-hopt { background: var(--row-bg); }

/* THE COLUMN-OPTIONS TRIGGER COSTS TRACK ONLY WHILE IT IS ON SCREEN — on a
   RIGHT-ALIGNED head, and only there.

   A left-aligned head is content-bound (its column is sized by its widest cell,
   not by its label), so taking the trigger out of flow there releases exactly
   0px and re-introduces the overlap defect this file used to ship. The two
   right-aligned heads are the opposite case: measured at 1440, SCORE was 68px
   holding a 40px label button, a 28px trigger at opacity 0, and 20px of ink in
   its cells; CREATED was 88 for a widest value of "12h". 56px of two narrow
   columns reserved for an affordance nobody can see until they point at it,
   taken out of the pool SUBJECT — the identifying field — was clipping from.

   So on a right-aligned head the trigger is an overlay pinned to the trailing
   rail with NO width at rest: no track spent, and no box for
   'scripts/inbox-grid-check.mjs''s "no header label overlaps its own options
   trigger" gate to intersect. Hover, focus-within or an open menu gives it its
   '--h-control-sm' back and pushes the label's own trailing inset out by the
   same amount, so the two never overlap in either state. The label yields —
   it truncates rather than the control overlapping it, which is Notion's
   behaviour in the same situation — and the trigger's 'title' ("Score options")
   names the column while it is doing so.

   It stays focusable at rest: the box is zero-width, not 'display:none', so
   'headerStops()' can still put the roving ring on it, and landing there makes
   the cell ':focus-within', which opens the rail before the reader sees it. */
.af-dt-hcell-r > .af-dt-hopt-rail { position: absolute; inset-inline-end: var(--space-1);
  top: 50%; transform: translateY(-50%); z-index: 2; }
.af-dt-hcell-r > .af-dt-hopt-rail > .af-dt-hopt { inline-size: 0; overflow: hidden; }
.af-dt-hcell-r:hover > .af-dt-hopt-rail > .af-dt-hopt,
.af-dt-hcell-r:focus-within > .af-dt-hopt-rail > .af-dt-hopt,
.af-dt-hcell-r:has([data-state='open']) > .af-dt-hopt-rail > .af-dt-hopt {
  inline-size: var(--h-control-sm); }
/* MARGIN, NOT PADDING. The sort control is 'flex-1', so its box spans the whole
   head; padding would clear the label but leave the BUTTON underneath the
   revealed trigger, and a click at the middle of the head would open the menu
   instead of sorting. A margin takes the trigger's track out of the button
   itself, so the two controls own disjoint boxes in both states — which is also
   what keeps 'inbox-grid-check''s header-sort click landing where it aims. */
.af-dt-hcell-r:hover > [data-header-sort],
.af-dt-hcell-r:focus-within > [data-header-sort],
.af-dt-hcell-r:has([data-state='open']) > [data-header-sort] {
  margin-inline-end: var(--h-control-sm); }

.af-dt-resize { position: absolute; top: 0; right: 0; height: 100%; width: 9px;
  transform: translateX(50%); cursor: col-resize; touch-action: none; user-select: none; z-index: 2; }
.af-dt-resize::after { content: ''; position: absolute; left: 50%; top: 20%; bottom: 20%; width: 1px;
  transform: translateX(-50%); background: var(--line-strong); opacity: 0;
  transition: opacity var(--dur-fast) var(--ease-out); }
.af-dt-hcell:hover .af-dt-resize::after { opacity: 1; }
/* The handle is a roving focus stop now (see 'headerStops'), so it owes a
   visible ring and a visible rule when the keyboard reaches it — the pointer
   already gets both from the hover rule above. */
.af-dt-resize:focus-visible { outline: 2px solid var(--focus); outline-offset: -1px;
  border-radius: var(--radius-xs); }
.af-dt-resize:focus-visible::after,
.af-dt-resize[data-resizing='true']::after { opacity: 1; top: 0; bottom: 0; width: 2px; background: var(--focus); }

.af-dt-hcell[data-dragging='true'] { opacity: 0.4; }
.af-dt-hcell[data-drop]::before { content: ''; position: absolute; top: 0; bottom: 0; width: 2px;
  background: var(--focus); z-index: 3; }
.af-dt-hcell[data-drop='before']::before { left: 0; }
.af-dt-hcell[data-drop='after']::before { right: 0; }

/* The travelling pip is '--text-muted', not '--accent'. "More rows are on the
   way" is progress, not a create — and this bar runs the full width of the grid
   at the bottom of the window, so gold here put a 1208px brand-coloured band on
   screen every time the list paged. Gold means create and nothing else. */
@keyframes af-dt-loadbar { from { transform: translateX(-100%); } to { transform: translateX(500%); } }
.af-dt-loadbar { position: relative; overflow: hidden; background: var(--line-subtle); }
.af-dt-loadbar::after { content: ''; position: absolute; inset: 0; width: 20%;
  background: var(--text-muted); animation: af-dt-loadbar 1.2s var(--ease-in-out) infinite; }
`;

/* ── component ────────────────────────────────────────────────────────────── */

export function DataTable<T>({
  data,
  columns,
  getRowId,
  rowCount,
  selection,
  sorting,
  columnState,
  onRowClick,
  onRowOpen,
  onRowPreview,
  activeRowId,
  loading = false,
  loadingRows = 12,
  empty,
  filteredEmpty,
  isFiltered = false,
  error,
  onRetry,
  errorState,
  onLoadMore,
  hasMore = false,
  isLoadingMore = false,
  floatingBarOpen = false,
  rowNoun,
  density = 'comfortable',
  stickyFirstColumn = false,
  showColumnPicker = true,
  footer,
  rowActions,
  rowActionsLabel,
  rowLabel,
  renderRowLead,
  showRowIndex = true,
  onEscape,
  className,
  id,
  label,
}: DataTableProps<T>) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const headRef = useRef<HTMLDivElement>(null);
  const shiftRef = useRef(false);
  const anchorRef = useRef<number | null>(null);
  const resizingRef = useRef(false);
  const interactingRef = useRef(false);
  // Read through a ref so the keydown handler stays referentially stable — it is
  // the identity every memoised row depends on.
  const onEscapeRef = useRef(onEscape);
  onEscapeRef.current = onEscape;

  const rowHeight = useMemo(
    () =>
      readPxVar(density === 'compact' ? '--h-row-compact' : '--h-row', ROW_HEIGHT_FALLBACK[density]),
    [density],
  );

  /* ── column state ───────────────────────────────────────────────────────── */

  const [orderState, setOrderState] = useState<ColumnOrderState>(columnState?.order ?? []);
  const [visibilityState, setVisibilityState] = useState<VisibilityState>(
    columnState?.visibility ?? {},
  );
  const [sizingState, setSizingState] = useState<ColumnSizingState>(columnState?.sizing ?? {});

  const externalOrder = columnState?.order;
  const externalVisibility = columnState?.visibility;
  const externalSizing = columnState?.sizing;

  useEffect(() => {
    if (externalOrder) setOrderState(externalOrder);
  }, [externalOrder]);
  useEffect(() => {
    if (externalVisibility) setVisibilityState(externalVisibility);
  }, [externalVisibility]);
  useEffect(() => {
    if (externalSizing) setSizingState(externalSizing);
  }, [externalSizing]);

  const onColumnStateChange = columnState?.onChange;
  const stateRef = useRef<DataTableColumnState>({ order: [], visibility: {}, sizing: {} });
  stateRef.current = { order: orderState, visibility: visibilityState, sizing: sizingState };

  const emitColumnState = useCallback(
    (patch: Partial<DataTableColumnState>) => {
      const next = { ...stateRef.current, ...patch };
      onColumnStateChange?.(next);
    },
    [onColumnStateChange],
  );

  /* ── sorting ────────────────────────────────────────────────────────────── */

  const controlledSorting = sorting !== undefined;
  const [innerSorting, setInnerSorting] = useState<SortingState>([]);

  const sortFieldByColumn = useMemo(() => {
    const map = new Map<string, string>();
    for (const column of columns) {
      const id = columnIdOf(column);
      if (id) map.set(id, column.meta?.sortField ?? id);
    }
    return map;
  }, [columns]);

  const columnBySortField = useMemo(() => {
    const map = new Map<string, string>();
    for (const [id, field] of sortFieldByColumn) map.set(field, id);
    return map;
  }, [sortFieldByColumn]);

  const sortingState = useMemo<SortingState>(() => {
    if (!sorting) return innerSorting;
    return sorting.value.map((spec) => ({
      id: columnBySortField.get(spec.field) ?? spec.field,
      desc: spec.direction === 'desc',
    }));
  }, [columnBySortField, innerSorting, sorting]);

  const sortingOnChange = sorting?.onChange;
  const applySorting = useCallback(
    (next: SortingState) => {
      if (controlledSorting) {
        sortingOnChange?.(
          next.map((entry) => ({
            field: sortFieldByColumn.get(entry.id) ?? entry.id,
            direction: entry.desc ? ('desc' as const) : ('asc' as const),
          })),
        );
      } else {
        setInnerSorting(next);
      }
    },
    [controlledSorting, sortFieldByColumn, sortingOnChange],
  );

  /** `sortKind` per column, so the header knows which way "first click" means. */
  const sortKindByColumn = useMemo(() => {
    const map = new Map<string, 'date' | 'number' | 'text'>();
    for (const column of columns) {
      const id = columnIdOf(column);
      if (id) map.set(id, column.meta?.sortKind ?? 'text');
    }
    return map;
  }, [columns]);

  /**
   * A plain click **toggles**; Shift+click builds a stack.
   *
   * It used to cycle asc → desc → none, and "none" is where it broke: the screen
   * resolves an empty sorting state back to its own default, so a third click on
   * the column the list already opens sorted by wrote the state it was already
   * in. `location.search` stayed empty, `aria-sort` stayed `descending`, the row
   * order never moved and there was no way to reach "Oldest first" from the
   * header at all. A header whose click does nothing is not a control.
   *
   * So: two states, and both of them visible. The direction the *first* click
   * picks comes from the column's `sortKind` — dates and numbers descending,
   * text ascending — because "newest first" and "highest score first" are what a
   * person means when they click CREATED or SCORE, and `asc` on a 913-row lead
   * inbox put score 3 on top. Clearing a sort remains reachable, where it
   * belongs: Shift+click drops a level out of a multi-column stack.
   */
  const cycleSort = useCallback(
    (columnId: string, additive: boolean) => {
      const current = sortingState.find((entry) => entry.id === columnId);
      const opening = sortKindByColumn.get(columnId) !== 'text';
      let next: SortingState;
      if (!current) {
        next = additive
          ? [...sortingState, { id: columnId, desc: opening }]
          : [{ id: columnId, desc: opening }];
      } else if (current.desc === opening) {
        // Second click: the other direction, never nothing.
        next = additive
          ? sortingState.map((entry) =>
              entry.id === columnId ? { ...entry, desc: !opening } : entry,
            )
          : [{ id: columnId, desc: !opening }];
      } else {
        next = additive
          ? sortingState.filter((entry) => entry.id !== columnId)
          : [{ id: columnId, desc: opening }];
      }
      applySorting(next);
    },
    [applySorting, sortKindByColumn, sortingState],
  );

  const setSort = useCallback(
    (columnId: string, desc: boolean) => applySorting([{ id: columnId, desc }]),
    [applySorting],
  );

  /* ── table ──────────────────────────────────────────────────────────────── */

  const sortedRowModel = useMemo(() => getSortedRowModel<T>(), []);

  const table = useReactTable<T>({
    data,
    columns,
    getRowId: (row) => getRowId(row),
    state: {
      sorting: sortingState,
      columnOrder: orderState,
      columnVisibility: visibilityState,
      columnSizing: sizingState,
    },
    defaultColumn: { size: 168, minSize: 64, maxSize: 720 },
    columnResizeMode: 'onChange',
    enableColumnResizing: true,
    manualSorting: controlledSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: controlledSorting ? undefined : sortedRowModel,
    onSortingChange: (updater) => {
      applySorting(typeof updater === 'function' ? updater(sortingState) : updater);
    },
    onColumnSizingChange: (updater) => {
      setSizingState((current) => (typeof updater === 'function' ? updater(current) : updater));
    },
    onColumnOrderChange: (updater) => {
      const next = typeof updater === 'function' ? updater(orderState) : updater;
      setOrderState(next);
      emitColumnState({ order: next });
    },
    onColumnVisibilityChange: (updater) => {
      const next = typeof updater === 'function' ? updater(visibilityState) : updater;
      setVisibilityState(next);
      emitColumnState({ visibility: next });
    },
  });

  const leafColumns = table.getAllLeafColumns();
  const headerGroup = table.getHeaderGroups()[0];
  const headers = headerGroup ? headerGroup.headers : [];
  const rows = table.getRowModel().rows;
  const totalColumnWidth = table.getTotalSize();

  // Declared here, not with the rest of the layout, because the keyboard handler
  // clamps ←/→ to the last data column.
  const visibleIds = leafColumns.filter((column) => column.getIsVisible()).map((c) => c.id);
  /**
   * The leading gutter is a column only when it holds something.
   *
   * With no selection checkbox, no lead renderer and no ordinal, it is a 30px
   * band of nothing — and as a `role=gridcell` it was still a stop on the row's
   * ←/→ ladder, announcing an empty accessible name and doing nothing on Enter.
   * When it is inert it drops out of the grid's ARIA entirely: no role, no
   * colindex, no roving stop, and every data column moves up one index so
   * `aria-colcount` and the indices still agree.
   */
  const leadInert = selection === undefined && renderRowLead === undefined && !showRowIndex;
  const colOffset = leadInert ? 1 : 2;
  /**
   * The trailing gutter is a cell only when it holds something — the same rule
   * the leading one already follows, and for the same reason.
   *
   * It holds two different things in two different rows: the row-action rail in
   * the body, the Columns picker in the header. Held by neither, it was still a
   * `role=gridcell` at the end of every row with an empty accessible name — one
   * empty announcement per row, and the last stop of the ←/→ ladder spent on
   * nothing at all.
   *
   * So the two halves are decided separately. A grid with no `rowActions` drops
   * the cell from its ROWS (`trailInert`), which also pulls the ArrowRight clamp
   * in by one so → cannot land on a stop that is not there. The header keeps its
   * cell for as long as the picker needs somewhere to live, and only when both
   * are gone does the column leave `aria-colcount` as well.
   *
   * When the body cell IS live it takes a name — `Row actions` by default,
   * `rowActionsLabel` per row where the caller has a better one. The `IconButton`
   * inside already names itself; the cell wrapper is what receives the roving
   * focus, and it was the thing announcing nothing.
   */
  const trailInert = rowActions === undefined;
  const trailHeaderInert = trailInert && !showColumnPicker;
  const columnCount = visibleIds.length + colOffset - (trailHeaderInert ? 1 : 0);
  /** The last column ←/→ can reach in a BODY row, which is one less without a rail. */
  const lastRowColumn = visibleIds.length + colOffset - (trailInert ? 1 : 0);

  // Persist a resize when the drag ends, not on every pointermove.
  const isResizingColumn = table.getState().columnSizingInfo.isResizingColumn;
  const wasResizing = useRef<string | false>(false);
  useEffect(() => {
    if (wasResizing.current && !isResizingColumn) {
      resizingRef.current = false;
      emitColumnState({});
    }
    wasResizing.current = isResizingColumn;
  }, [emitColumnState, isResizingColumn]);

  /* ── virtualisation ─────────────────────────────────────────────────────── */

  const [scrollMargin, setScrollMargin] = useState(0);

  useLayoutEffect(() => {
    const body = bodyRef.current;
    if (!body) return;
    const update = () => setScrollMargin(body.offsetTop);
    update();
    const observer = new ResizeObserver(update);
    if (headRef.current) observer.observe(headRef.current);
    observer.observe(body);
    return () => observer.disconnect();
  }, []);

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => rowHeight,
    overscan: OVERSCAN,
    scrollMargin,
    // The header row is sticky, so the first --h-row-compact of the scrollport is
    // covered: without this, Home/PageUp park the focused row underneath it.
    scrollPaddingStart: readPxVar('--h-row-compact', 32),
    getItemKey: (index) => rows[index]?.id ?? index,
  });

  const virtualItems = virtualizer.getVirtualItems();
  const totalSize = virtualizer.getTotalSize();

  const handleScroll = useCallback((event: ReactUIEvent<HTMLDivElement>) => {
    const element = event.currentTarget;
    element.dataset.scrolledX = element.scrollLeft > 0 ? 'true' : 'false';
    // Published as a variable rather than as state: the summary rail follows the
    // columns sideways without a render per scroll frame.
    element.parentElement?.style.setProperty('--af-dt-x', `${-element.scrollLeft}px`);
  }, []);

  /* ── selection ──────────────────────────────────────────────────────────── */

  const selectable = selection !== undefined;
  const selectedSet = selection?.selected;
  const selectionOnChange = selection?.onChange;
  const enableRange = selection?.enableRange ?? true;

  const { allLoadedSelected, someLoadedSelected } = useMemo(() => {
    if (!selectedSet || data.length === 0) {
      return { allLoadedSelected: false, someLoadedSelected: false };
    }
    let hit = 0;
    for (const row of data) if (selectedSet.has(getRowId(row))) hit += 1;
    return { allLoadedSelected: hit === data.length, someLoadedSelected: hit > 0 };
  }, [data, getRowId, selectedSet]);

  const toggleRow = useCallback(
    (index: number, withRange: boolean) => {
      if (!selectedSet || !selectionOnChange) return;
      const target = rows[index];
      if (!target) return;
      const next = new Set(selectedSet);

      if (withRange && enableRange && anchorRef.current !== null) {
        const [from, to] = [anchorRef.current, index].sort((a, b) => a - b);
        const shouldSelect = !next.has(target.id);
        for (let i = from; i <= to; i += 1) {
          const id = rows[i]?.id;
          if (!id) continue;
          if (shouldSelect) next.add(id);
          else next.delete(id);
        }
      } else {
        if (next.has(target.id)) next.delete(target.id);
        else next.add(target.id);
        anchorRef.current = index;
      }
      selectionOnChange(next);
    },
    [enableRange, rows, selectedSet, selectionOnChange],
  );

  const toggleAllLoaded = useCallback(() => {
    if (!selectedSet || !selectionOnChange) return;
    const next = new Set(selectedSet);
    if (allLoadedSelected) for (const row of rows) next.delete(row.id);
    else for (const row of rows) next.add(row.id);
    anchorRef.current = null;
    selectionOnChange(next);
  }, [allLoadedSelected, rows, selectedSet, selectionOnChange]);

  /* ── row focus and keyboard ─────────────────────────────────────────────── */

  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);
  /**
   * The cell inside the focused row that holds focus, by `aria-colindex`, or
   * null while the row itself holds it. role=grid promises ←/→ cell navigation;
   * this is the state that keeps that promise.
   */
  const [focusedCol, setFocusedCol] = useState<number | null>(null);
  /** Read by the container's focus handler, which must not re-subscribe per row. */
  const focusedIndexRef = useRef<number | null>(focusedIndex);
  focusedIndexRef.current = focusedIndex;
  const virtualRange = virtualizer.range;
  const renderedSignature = `${virtualRange?.startIndex ?? -1}:${virtualRange?.endIndex ?? -1}`;

  useEffect(() => {
    if (focusedIndex === null || !interactingRef.current) return;
    /**
     * Never out of the HEADER.
     *
     * ArrowUp off row 1 parks focus on a header stop while `focusedIndex` goes
     * on naming the row it came from — that is how the header is reachable at
     * all. So any re-render that re-runs this effect while the header holds
     * focus hands focus back to that row, and the select-all box is exactly
     * where that happens: Space on it raises the bulk bar, the bar's berth
     * comes off the scroll viewport, the viewport resize re-ranges the
     * virtualiser, `renderedSignature` changes, and focus lands on row 1.
     * Measured: a second Space then selected row 1 instead of clearing the
     * selection, and the next Enter opened a record. Mouse users could
     * un-select-all; keyboard users could not.
     */
    if (headRef.current?.contains(document.activeElement)) return;
    const rowElement = bodyRef.current?.querySelector<HTMLElement>(
      `[data-row-index="${focusedIndex}"]`,
    );
    if (!rowElement) return;
    const cell =
      focusedCol === null
        ? null
        : rowElement.querySelector<HTMLElement>(`[data-col-index="${focusedCol}"]`);
    // Actionable mode: focus is on a widget INSIDE the cell this effect is
    // aiming at (Enter/F2 put it there). Re-focusing the cell on a scroll frame
    // would throw the user out of the control they are operating. The guard is
    // deliberately scoped to the CELL: when the target is the row — ← out of
    // column 1, or Escape out of a cell — the move outward is the point, and
    // the row contains the cell that currently holds focus.
    if (cell?.contains(document.activeElement)) return;
    (cell ?? rowElement).focus({ preventScroll: true });
  }, [focusedCol, focusedIndex, renderedSignature]);

  /**
   * The grid is ONE tab stop, all the way down.
   *
   * The header cells have always honoured that — every control in them is
   * `tabIndex -1` and is reached with the arrow keys. The BODY cells were left
   * at the browser default, so a grid rendering 32 rows × 5 in-row controls put
   * 160 stops between the toolbar and whatever follows the table, and the count
   * grew as the virtualiser mounted more rows. Callers declare `tabIndex={-1}`
   * on the controls they own; this sweep is the invariant behind them, so no
   * cell widget anywhere can put the page back to 129 tab presses. Reach them
   * with → then Enter/F2, and leave with Escape.
   */
  useEffect(() => {
    const body = bodyRef.current;
    if (!body) return;
    for (const node of body.querySelectorAll<HTMLElement>(
      '[role="gridcell"] a[href], [role="gridcell"] button, [role="gridcell"] input,' +
        ' [role="gridcell"] select, [role="gridcell"] textarea, [role="gridcell"] [tabindex="0"]',
    )) {
      if (node.tabIndex >= 0) node.tabIndex = -1;
    }
  });

  useEffect(() => {
    if (focusedIndex !== null && focusedIndex > rows.length - 1) {
      setFocusedIndex(rows.length > 0 ? rows.length - 1 : null);
    }
  }, [focusedIndex, rows.length]);

  const moveFocus = useCallback(
    (to: number) => {
      if (rows.length === 0) return;
      const next = Math.max(0, Math.min(rows.length - 1, to));
      interactingRef.current = true;
      virtualizer.scrollToIndex(next, { align: 'auto' });
      setFocusedIndex(next);
    },
    [rows.length, virtualizer],
  );

  const openRow = useCallback(
    (index: number) => {
      const row = rows[index];
      if (row) (onRowOpen ?? onRowClick)?.(row.original);
    },
    [onRowClick, onRowOpen, rows],
  );

  /**
   * The header is reached with the arrow keys, not with Tab — see the note on
   * the sort button. `data-header-sort` marks the roving stop per column;
   * `data-header-select` marks the select-all box in the ordinal gutter, which
   * is stop 0 when the grid is selectable so it is reachable without costing the
   * page a tab stop of its own.
   *
   * One stop per header cell, and never a disabled node: `HTMLElement.focus()`
   * on a disabled button is a no-op, so a grid whose columns cannot sort used to
   * swallow ArrowUp out of row 1 and leave the header — its sort control, its
   * options menu and its resize handle — reachable by mouse only. An
   * unsortable column falls back to its options button, which is always live.
   */
  const headerStops = useCallback(() => {
    const head = headRef.current;
    if (!head) return [];
    const stops: HTMLElement[] = [];
    for (const cell of Array.from(head.querySelectorAll<HTMLElement>('[role="columnheader"]'))) {
      const stop =
        cell.querySelector<HTMLElement>('[data-header-select]:not([disabled])') ??
        cell.querySelector<HTMLElement>('[data-header-sort]:not([disabled])') ??
        cell.querySelector<HTMLElement>('[data-header-menu]:not([disabled])');
      if (stop) stops.push(stop);
      // …then the column's resize handle, so widening a truncating column is a
      // keyboard operation and not a drag. It sits AFTER the cell's own control
      // so ←/→ still walk the columns in order: label, its edge, next label.
      const resize = cell.querySelector<HTMLElement>('[data-header-resize]');
      if (resize) stops.push(resize);
    }
    return stops;
  }, []);

  /** Header stop index ↔ `aria-colindex`: the select-all stop is colindex 1. */
  const stopOffset = selectable ? 1 : 0;

  const focusHeader = useCallback(
    (index: number) => {
      const stops = headerStops();
      if (stops.length === 0) return false;
      const clamped = Math.max(0, Math.min(stops.length - 1, index));
      stops[clamped]?.focus();
      return true;
    },
    [headerStops],
  );

  const handleHeaderKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLDivElement>, cell: HTMLElement): boolean => {
      const stops = headerStops();
      const active = document.activeElement as HTMLElement | null;
      // The ACTIVE stop first, and only then the cell's first stop. A header
      // cell contributes two stops now (its control and its resize handle), so
      // "the first stop this cell contains" is no longer the one the reader is
      // on — with that lookup ArrowRight from a resize handle walked back to the
      // control beside it and the ring bounced between two stops forever.
      const activeIndex = active ? stops.indexOf(active) : -1;
      const index =
        activeIndex >= 0
          ? activeIndex
          : Math.max(0, stops.findIndex((stop) => cell.contains(stop)));

      if (event.altKey && event.key === 'ArrowDown') {
        event.preventDefault();
        const trigger = cell.querySelector<HTMLElement>('[data-header-menu]');
        if (trigger) {
          // The menu trigger opens on its own key handling, not on a synthetic
          // click, so hand it the keystroke it expects.
          trigger.focus();
          trigger.dispatchEvent(
            new KeyboardEvent('keydown', { key: 'ArrowDown', bubbles: true, cancelable: true }),
          );
        }
        return true;
      }
      if (event.metaKey || event.ctrlKey || event.altKey) return false;

      switch (event.key) {
        case 'ArrowRight':
          event.preventDefault();
          focusHeader(index + 1);
          return true;
        case 'ArrowLeft':
          event.preventDefault();
          focusHeader(index - 1);
          return true;
        case 'Home':
          event.preventDefault();
          focusHeader(0);
          return true;
        case 'End':
          event.preventDefault();
          focusHeader(stops.length - 1);
          return true;
        case 'ArrowDown': {
          event.preventDefault();
          // The cell's OWN `aria-colindex`, not an arithmetic on the stop index:
          // a header cell now contributes two stops (its control and its resize
          // handle), so "stop N is column N" stopped being true. The gutter's
          // select-all cell carries colindex 1, which is what it always mapped
          // to.
          const declared = Number(cell.getAttribute('aria-colindex'));
          setFocusedCol(
            Number.isFinite(declared) && declared > 0
              ? declared
              : stopOffset === 1 && index === 0
                ? 1
                : index - stopOffset + 2,
          );
          moveFocus(focusedIndex ?? 0);
          return true;
        }
        default:
          return false;
      }
    },
    [focusHeader, focusedIndex, headerStops, moveFocus, stopOffset],
  );

  const handleKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLDivElement>) => {
      const target = event.target as HTMLElement;
      if (target.closest('input, textarea, select, [contenteditable="true"]')) return;

      /**
       * Only keys pressed inside the grid's own DOM.
       *
       * A cell's menu, dialog or tooltip renders in a PORTAL — outside this
       * subtree in the document, but still inside it in the React tree, so its
       * synthetic keydown bubbles here. Escape inside an open stage menu was
       * therefore doing two things at once: closing the menu, and running the
       * grid's own Escape, which dropped the cell focus and threw the user back
       * to the row. An overlay owns its own keys; the grid gets them back when
       * focus returns to the grid.
       */
      if (!event.currentTarget.contains(target)) return;

      const headerCell = target.closest<HTMLElement>('[role="columnheader"]');
      if (headerCell && headRef.current?.contains(headerCell)) {
        handleHeaderKeyDown(event, headerCell);
        return;
      }

      /**
       * Actionable mode — the grid pattern's second half.
       *
       * Focus is on a widget inside a cell, put there by Enter or F2 below.
       * While it is, the widget owns the keyboard: j must type into nothing, ↓
       * must open the menu that is focused, Space must press the button. The
       * grid takes exactly one key back — Escape, which returns focus to the
       * cell and hands navigation back. This is the same bargain that buys the
       * in-row controls their place outside the page's tab ring.
       */
      const widgetCell = target.closest<HTMLElement>('[role="gridcell"]');
      if (widgetCell && widgetCell !== target && bodyRef.current?.contains(widgetCell)) {
        if (event.key === 'Escape') {
          event.preventDefault();
          event.stopPropagation();
          widgetCell.focus();
        }
        return;
      }

      if (event.metaKey || event.ctrlKey || event.altKey) return;

      const current = focusedIndex ?? -1;
      switch (event.key) {
        case 'ArrowDown':
        case 'j': {
          event.preventDefault();
          const next = current + 1;
          if (event.shiftKey && current >= 0) toggleRow(Math.min(next, rows.length - 1), true);
          moveFocus(next);
          break;
        }
        case 'ArrowUp':
        case 'k': {
          event.preventDefault();
          // ArrowUp off the first row lands in the column headers, per the grid
          // pattern — that is how the sort controls are reached at all, and it
          // lands on the header of the column the focus was already in.
          if (
            current === 0 &&
            !event.shiftKey &&
            focusHeader(
              focusedCol === null
                ? stopOffset
                : focusedCol <= 1
                  ? 0
                  : focusedCol - 2 + stopOffset,
            )
          ) {
            break;
          }
          const next = current <= 0 ? 0 : current - 1;
          if (event.shiftKey && current >= 0) toggleRow(next, true);
          moveFocus(next);
          break;
        }
        case 'Home':
          event.preventDefault();
          moveFocus(0);
          break;
        case 'End':
          event.preventDefault();
          moveFocus(rows.length - 1);
          break;
        case 'PageDown':
          event.preventDefault();
          moveFocus(current + 10);
          break;
        case 'PageUp':
          event.preventDefault();
          moveFocus(current - 10);
          break;
        case 'x':
          if (current < 0) break;
          event.preventDefault();
          toggleRow(current, event.shiftKey);
          break;
        /*
         * "." — the row's own actions, from the row.
         *
         * `lib/keyboard` has declared this key on the `list` scope all along and
         * the shortcut sheet has printed it on every list surface, but only the
         * Tasks screen — which rolls its own grid — ever bound it. On the three
         * surfaces that share THIS grid the only keyboard route to a row menu
         * was → eight times to the trailing gutter and then Enter, which is the
         * kind of path a person finds once and never again. Bound here, so it
         * holds on every grid the product mounts rather than on the one a
         * reviewer opened.
         *
         * The trigger opens on its own key handling, not on a synthetic click,
         * so it is handed the keystroke it expects — the same bargain the
         * Enter-into-a-cell branch above makes. Closing the menu hands focus to
         * the gridcell (see `Menu`'s `onCloseAutoFocus`), which is where the
         * roving navigation can pick it up again.
         */
        case '.': {
          if (current < 0) break;
          const trigger = bodyRef.current?.querySelector<HTMLElement>(
            `[data-row-index="${current}"] [data-col-index="${lastRowColumn}"] button[aria-haspopup="menu"]`,
          );
          if (!trigger) break;
          event.preventDefault();
          trigger.focus({ preventScroll: true });
          trigger.dispatchEvent(
            new KeyboardEvent('keydown', { key: 'Enter', bubbles: true, cancelable: true }),
          );
          break;
        }
        case 'F2':
        case 'Enter': {
          if (current < 0) break;
          event.preventDefault();
          // Inside a cell, Enter (or F2) enters ACTIONABLE MODE: focus moves to
          // the first widget in that cell and stays there until Escape hands it
          // back. That is what buys those controls their place outside the
          // page's tab ring — the same mechanism the header row already uses.
          // On the row itself Enter opens the record, as before; F2 is a cell
          // key only and does nothing there.
          if (focusedCol !== null) {
            const cell = bodyRef.current?.querySelector<HTMLElement>(
              `[data-row-index="${current}"] [data-col-index="${focusedCol}"]`,
            );
            const control = cell?.querySelector<HTMLElement>(
              'a[href], button:not([disabled]), [role="checkbox"]:not([disabled])',
            );
            if (control) {
              control.focus();
              if (event.key === 'Enter' && control.getAttribute('aria-haspopup') === 'menu') {
                // A menu trigger opens on its own key handling, not on a
                // synthetic click, so hand it the keystroke it expects. Closing
                // the menu returns focus to the trigger, which is where
                // actionable mode left it; Escape then steps back to the cell.
                control.dispatchEvent(
                  new KeyboardEvent('keydown', { key: 'Enter', bubbles: true, cancelable: true }),
                );
              }
              break;
            }
          }
          if (event.key === 'F2') break;
          openRow(current);
          break;
        }
        case 'ArrowRight': {
          // Row focus → first cell → across the row, per the WAI-ARIA grid
          // pattern the role announces. The clamp is `lastRowColumn`, not
          // `aria-colcount`: the trailing gutter is a stop only when it carries
          // the action rail, and on a grid without one → used to walk into a
          // cell that is not in the row.
          if (current < 0) break;
          event.preventDefault();
          setFocusedCol((col) => (col === null ? 1 : Math.min(lastRowColumn, col + 1)));
          break;
        }
        case 'ArrowLeft': {
          if (current < 0) break;
          event.preventDefault();
          setFocusedCol((col) => (col === null || col <= 1 ? null : col - 1));
          break;
        }
        case 'Escape': {
          // A staged dismiss, not a single exit. Inside a cell, Esc steps back
          // out to the row. Then the owner gets a turn — with rows ticked it
          // drops the selection and focus stays exactly where it is. Only with
          // nothing left to undo is Esc left to bubble, so the screen can hand
          // focus back to its filter.
          if (focusedCol !== null) {
            event.preventDefault();
            setFocusedCol(null);
            /*
             * And move focus NOW, not on the effect that follows the state
             * change. That effect is the general re-seater and it is gated on
             * `interactingRef` and on the virtualiser's rendered range: if the
             * frame that processes this Escape also recycles the focused cell —
             * an SSE row arriving on /inbox, a background refetch on
             * /companies — the cell is detached before the effect runs, blur
             * fires with a null `relatedTarget`, `interactingRef` goes false
             * and the effect returns early. Focus lands on <body> and the
             * reader's place in a 319-row list is gone. Measured: the second
             * rung of the menu → cell → row ladder failed on a different route
             * on each run of `scripts/invariants-check.mjs`. Stepping out of a
             * cell is a synchronous, unconditional move to the row it is in.
             */
            interactingRef.current = true;
            bodyRef.current
              ?.querySelector<HTMLElement>(`[data-row-index="${current}"]`)
              ?.focus({ preventScroll: true });
            break;
          }
          if (onEscapeRef.current?.()) {
            event.preventDefault();
            break;
          }
          break;
        }
        case ' ': {
          const row = rows[current];
          if (!row) break;
          if (onRowPreview) {
            event.preventDefault();
            onRowPreview(row.original);
            break;
          }
          // No preview on this grid: Space is the checkbox it looks like, so the
          // per-row box needs no tab stop of its own.
          if (selectable) {
            event.preventDefault();
            toggleRow(current, event.shiftKey);
          }
          break;
        }
        default:
          break;
      }
    },
    [
      lastRowColumn,
      focusHeader,
      focusedCol,
      focusedIndex,
      handleHeaderKeyDown,
      moveFocus,
      onRowPreview,
      openRow,
      rows,
      selectable,
      stopOffset,
      toggleRow,
    ],
  );

  /* ── stable row callbacks ───────────────────────────────────────────────── */

  const rowActionsRef = useRef(rowActions);
  rowActionsRef.current = rowActions;
  const rowActionsLabelRef = useRef(rowActionsLabel);
  rowActionsLabelRef.current = rowActionsLabel;
  const rowLabelRef = useRef(rowLabel);
  rowLabelRef.current = rowLabel;
  const renderLeadRef = useRef(renderRowLead);
  renderLeadRef.current = renderRowLead;
  const onRowClickRef = useRef(onRowClick);
  onRowClickRef.current = onRowClick;

  const rowsClickable = onRowClick !== undefined || onRowOpen !== undefined;

  const renderActions = useCallback((row: T) => rowActionsRef.current?.(row) ?? null, []);
  const renderActionsLabel = useCallback(
    (row: T) => rowActionsLabelRef.current?.(row) ?? 'Row actions',
    [],
  );
  /** "Select AF-1906" where the grid knows what a row is called; "Select row 7"
      where it does not. Never a bare ordinal when an identity is available. */
  const renderSelectLabel = useCallback(
    (row: T, index: number) => {
      const name = rowLabelRef.current?.(row);
      return name ? `Select ${name}` : `Select row ${index + 1}`;
    },
    [],
  );
  const renderLead = useCallback(
    (row: T, index: number) => renderLeadRef.current?.(row, index) ?? null,
    [],
  );

  const handleRowClick = useCallback(
    (index: number) => {
      interactingRef.current = true;
      setFocusedCol(null);
      setFocusedIndex(index);
      const row = rows[index];
      if (row) onRowClickRef.current?.(row.original);
    },
    [rows],
  );

  const handleRowToggle = useCallback(
    (index: number) => toggleRow(index, shiftRef.current),
    [toggleRow],
  );

  const handleRowDoubleClick = useCallback((index: number) => openRow(index), [openRow]);

  const captureShift = useCallback((event: ReactPointerEvent<HTMLElement>) => {
    shiftRef.current = event.shiftKey;
  }, []);

  /* ── infinite scroll ────────────────────────────────────────────────────── */

  const sentinelRef = useRef<HTMLDivElement>(null);
  const loadMoreRef = useRef(onLoadMore);
  loadMoreRef.current = onLoadMore;

  useEffect(() => {
    const node = sentinelRef.current;
    const root = scrollRef.current;
    if (!node || !root || !hasMore || loading) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) loadMoreRef.current?.();
      },
      { root, rootMargin: '480px 0px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, loading, rows.length]);

  /* ── column reorder ─────────────────────────────────────────────────────── */

  const [dragState, setDragState] = useState<DragState | null>(null);

  const currentOrder = useCallback(
    () => (orderState.length ? orderState : leafColumns.map((column) => column.id)),
    [leafColumns, orderState],
  );

  const moveColumn = useCallback(
    (sourceId: string, targetId: string, after: boolean) => {
      if (sourceId === targetId) return;
      const order = currentOrder().filter((id) => id !== sourceId);
      const targetIndex = order.indexOf(targetId);
      if (targetIndex < 0) return;
      order.splice(after ? targetIndex + 1 : targetIndex, 0, sourceId);
      table.setColumnOrder(order);
    },
    [currentOrder, table],
  );

  const shiftColumn = useCallback(
    (columnId: string, delta: number) => {
      const order = currentOrder();
      const from = order.indexOf(columnId);
      const to = from + delta;
      if (from < 0 || to < 0 || to >= order.length) return;
      const next = order.slice();
      next.splice(to, 0, next.splice(from, 1)[0]);
      table.setColumnOrder(next);
    },
    [currentOrder, table],
  );

  const resetColumns = useCallback(() => {
    table.setColumnOrder([]);
    table.setColumnVisibility({});
    table.setColumnSizing({});
    setSizingState({});
    onColumnStateChange?.({ order: [], visibility: {}, sizing: {} });
  }, [onColumnStateChange, table]);

  /* ── layout ─────────────────────────────────────────────────────────────── */

  /* ── surplus width ──────────────────────────────────────────────────────── */

  /**
   * Whatever the columns do not use is split between the `meta.flex` columns by
   * weight rather than left as a trailing gutter, so the same ten columns fill
   * 1440 and 1920 and a wider monitor buys more text, not more empty space.
   *
   * Each column's share is capped at its own `maxSize`, and whatever a capped
   * column cannot take is re-offered to the ones still under their cap. Without
   * the cap the single widest-weighted column eats the entire surplus — 480px of
   * white space inside one cell — which is the same defect as the trailing
   * gutter, just parked somewhere less obvious.
   */
  const flexColumns: { id: string; weight: number; headroom: number }[] = [];
  for (const column of leafColumns) {
    if (!column.getIsVisible()) continue;
    const declared = column.columnDef.meta?.flex;
    const weight = declared === true ? 1 : typeof declared === 'number' ? declared : 0;
    if (weight <= 0) continue;
    const max = column.columnDef.maxSize;
    const headroom =
      typeof max === 'number' && Number.isFinite(max)
        ? Math.max(0, max - column.getSize())
        : Number.POSITIVE_INFINITY;
    flexColumns.push({ id: column.id, weight, headroom });
  }
  const weightSignature = flexColumns
    .map((entry) => `${entry.id}:${entry.weight}:${entry.headroom}`)
    .join(',');
  let totalWeight = 0;
  for (const entry of flexColumns) totalWeight += entry.weight;

  const [viewport, setViewport] = useState({ width: 0, height: 0 });
  useLayoutEffect(() => {
    const node = scrollRef.current;
    if (!node) return;
    const update = () =>
      setViewport((current) =>
        current.width === node.clientWidth && current.height === node.clientHeight
          ? current
          : { width: node.clientWidth, height: node.clientHeight },
      );
    update();
    const observer = new ResizeObserver(update);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const gutterWidth = useMemo(() => readPxVar('--h-control', 30), []);
  const headerHeight = useMemo(() => readPxVar('--h-row-compact', 32), []);

  /**
   * The scroll port's own gutter — the same `--space-3` `PageHeader` and
   * `ListToolbar` hang off, so the hairline under the header and the divider
   * under every row stop where the toolbar's controls stop instead of running
   * into the window edge.
   *
   * It is conditional, and only on the one thing that could make it wrong: a
   * table whose columns are already at their minimum has no 24px to give, and a
   * hairline that stops 12px early is worth a great deal less than a horizontal
   * scrollbar. `clientWidth` is the padding box, so it does not move when the
   * padding does — no feedback loop.
   */
  const insetWidth = useMemo(() => readPxVar('--space-3', 12), []);
  const columnsWidth = totalColumnWidth + gutterWidth * 2;
  const inset =
    viewport.width === 0 || viewport.width - insetWidth * 2 >= columnsWidth ? insetWidth : 0;

  const surplus =
    totalWeight > 0 && viewport.width > 0
      ? Math.max(0, viewport.width - (columnsWidth + inset * 2))
      : 0;

  /**
   * Water-filling: hand out the surplus by weight, take back whatever a column
   * cannot hold, and re-offer it to the rest until nobody has headroom left.
   * Floored per column so the distributed total can never exceed the surplus and
   * mint a 1px horizontal scrollbar.
   */
  const growth = new Map<string, number>();
  let distributed = 0;
  {
    let remaining = surplus;
    let active = flexColumns.filter((entry) => entry.headroom > 0);
    while (remaining > 0 && active.length > 0) {
      let activeWeight = 0;
      for (const entry of active) activeWeight += entry.weight;
      if (activeWeight <= 0) break;
      let spent = 0;
      const next: typeof active = [];
      for (const entry of active) {
        const taken = growth.get(entry.id) ?? 0;
        const share = Math.floor((remaining * entry.weight) / activeWeight);
        const give = Math.min(share, entry.headroom - taken);
        if (give > 0) {
          growth.set(entry.id, taken + give);
          spent += give;
        }
        if ((growth.get(entry.id) ?? 0) < entry.headroom) next.push(entry);
      }
      if (spent === 0) break;
      remaining -= spent;
      active = next;
    }
    distributed = surplus - remaining;
  }

  const growthRef = useRef(growth);
  growthRef.current = growth;
  const widthOf = useCallback(
    (columnId: string, size: number) => size + (growthRef.current.get(columnId) ?? 0),
    // `weightSignature` and `surplus` are the dependencies; the map itself is
    // read through a ref so a memoised row is not re-rendered by a new identity.
    [surplus, totalWeight, weightSignature],
  );

  /** Enough shimmer to reach the fold, exactly as the loaded rows will. */
  const skeletonRowCount =
    viewport.height > 0
      ? Math.max(1, Math.ceil((viewport.height - headerHeight) / rowHeight) + 1)
      : loadingRows;

  const layoutKey = `${density}|${totalColumnWidth}|${distributed}|${visibleIds.join(',')}`;
  const contentMinWidth = `calc(${totalColumnWidth + distributed}px + ${GUTTER} * 2)`;

  const hasRows = rows.length > 0;
  const hasError = error !== undefined && error !== null;
  /**
   * The one line the failed-append strip carries.
   *
   * Same rule `ErrorState` follows: a 4xx message is written for the reader
   * ("Too many requests — retry in 42s.") and is printed; a 5xx body is written
   * for the log and is replaced with a sentence, because a constraint name in a
   * 40px strip under a live list is debris.
   */
  const appendErrorMessage = useMemo(() => {
    if (!hasError) return null;
    const derived = deriveError(error);
    return derived.status !== null && derived.status >= 500
      ? 'We could not load any more of this list.'
      : derived.message;
  }, [error, hasError]);
  // Error wins over loading: a failed fetch that keeps painting skeletons is a
  // screen with no way out of it.
  const showSkeleton = loading && !hasRows && !hasError;
  const showEmpty = !loading && !hasError && !hasRows;
  const showFooter = footer !== undefined && hasRows && !hasError;
  const escalationEnabled = selectable && Boolean(selection?.onSelectAllMatching);
  const totalMatching = rowCount ?? data.length;
  const showEscalation =
    escalationEnabled &&
    ((allLoadedSelected && totalMatching > data.length) || Boolean(selection?.allMatching));

  return (
    <div
      className={cn(
        'relative flex min-h-0 min-w-0 flex-col overflow-hidden bg-[var(--surface-1)]',
        className,
      )}
    >
      <style href="af-datatable" precedence="component">
        {GRID_CSS}
      </style>

      <div
        ref={scrollRef}
        id={id}
        role="grid"
        aria-label={label}
        aria-rowcount={totalMatching + 1}
        aria-colcount={columnCount}
        // Space selects a row and Shift+↓ extends the range, so the grid is
        // multi-selectable; without this a screen reader announces the opposite
        // of what the keyboard actually does.
        aria-multiselectable={selectable ? true : undefined}
        aria-busy={loading || undefined}
        tabIndex={focusedIndex === null ? 0 : -1}
        onScroll={handleScroll}
        onKeyDown={handleKeyDown}
        onFocusCapture={() => {
          interactingRef.current = true;
        }}
        // Tab lands on the container; the container immediately hands focus to a
        // row, so the ring never disappears for a keystroke and the whole grid
        // costs the page exactly one tab stop.
        onFocus={(event) => {
          if (rows.length === 0) return;
          if (event.target !== event.currentTarget) {
            // A row that took focus on its own — a click, a programmatic
            // `.focus()`, the skip link — seeds the roving index from the DOM.
            // Without this the index starts at 0 wherever focus actually is, so
            // the first j/ArrowDown re-focused the row you were already on and
            // only the second one moved. Only the row element itself syncs; a
            // control inside a cell must keep the focus it just took.
            const row = (event.target as HTMLElement).closest<HTMLElement>('[data-row-index]');
            if (!row || row !== event.target) return;
            const index = Number(row.dataset.rowIndex);
            if (!Number.isInteger(index) || index === focusedIndexRef.current) return;
            setFocusedIndex(index);
            setFocusedCol(null);
            return;
          }
          const index = Math.min(focusedIndexRef.current ?? 0, rows.length - 1);
          setFocusedIndex(index);
          setFocusedCol(null);
          bodyRef.current
            ?.querySelector<HTMLElement>(`[data-row-index="${index}"]`)
            ?.focus({ preventScroll: true });
        }}
        onBlur={(event) => {
          if (event.currentTarget.contains(event.relatedTarget as Node | null)) return;
          /*
           * Focus left the grid. Whether that was a DECISION or an ACCIDENT is
           * the whole question, and `relatedTarget` alone cannot answer it:
           * clicking dead space and having your focused row deleted under you
           * both arrive as `relatedTarget === null`.
           *
           * The node itself can. A row or cell that is still in the document
           * lost focus because the reader took it somewhere — let go. A node
           * that is no longer connected was recycled by the virtualiser or
           * re-rendered away while it held focus, which is not a decision, it
           * is a dropped reader: <body> takes over, Tab goes to "Skip to
           * content", and the reader's place in the list is gone. Put them back
           * on the row the grid still believes they are on.
           */
          const leaving = event.target as HTMLElement | null;
          if (event.relatedTarget === null && leaving !== null && !leaving.isConnected) {
            const index = focusedIndexRef.current;
            if (index !== null) {
              requestAnimationFrame(() => {
                if (document.activeElement !== document.body) return;
                const row = bodyRef.current?.querySelector<HTMLElement>(
                  `[data-row-index="${index}"]`,
                );
                if (!row) return;
                interactingRef.current = true;
                row.focus({ preventScroll: true });
              });
              return;
            }
          }
          interactingRef.current = false;
        }}
        // Reserves the sticky header band for every scroll-into-view, native or
        // programmatic, so a focused row is never parked underneath it.
        style={{ scrollPaddingBlockStart: 'var(--h-row-compact)', paddingInline: inset }}
        data-bulk-open={floatingBarOpen ? 'true' : undefined}
        className="af-dt-scroll relative min-h-0 flex-1 overflow-auto overscroll-contain outline-none"
      >
        <div style={{ minWidth: contentMinWidth }}>
          {/* ── header ─────────────────────────────────────────────────── */}
          {/* `display: contents`, not a box: the header row is `position: sticky`
              against the scroll port, and a laid-out wrapper would make it stick
              inside its own 32px instead. The rowgroup exists for ARIA only.

              THE HEADER RULE, AND WHERE IT STOPS.

              A column header is the key to a set of rows, so it is drawn while
              there is data or a skeleton to explain. It is dropped where it
              explains nothing — and that is exactly TWO states, not every
              state with no rows in it:

                · the WHOLE-LIST ERROR, below;
                · the TRULY-EMPTY list, where nothing has ever arrived. There is
                  no table yet, only a block explaining where rows come from,
                  and eleven words of ruled-off chrome above it is what made
                  Contacts and Companies read as a list that had failed to
                  finish rendering.

              THE NARROWED EMPTY IS NOT ONE OF THEM, and treating it as one was
              a defect of its own. On `/inbox?q=zzzzqqqxxx`
              `document.querySelector('[role=columnheader]')` returned null: the
              reader lost every cue that this is a table with columns at the
              same moment the toolbar above kept Columns, Sort and the density
              control — three affordances acting on nothing visible. A filter
              that empties a list must not change the list's identity. Attio and
              Notion keep the head over an empty body for precisely this reason,
              and so does this grid now: the head stays, `isFiltered` marks the
              case, and the empty block mounts UNDER it inside the body region
              rather than in place of the whole grid. Its own top offset is
              measured from the head's bottom edge (`[data-under-head]` in
              GRID_CSS) so the headline lands at the same y in both empty
              states.

              Held in ONE place because the rule has to hold on every grid in
              the product, not on the two a reviewer happened to open.

              THE WHOLE-LIST ERROR IS ONE OF THOSE STATES. `showEmpty` is
              `!loading && !hasError && !hasRows`, so a failed list fell through
              this guard and kept its full chrome over zero rows: measured with
              the companies endpoint aborted, eleven live `[role=columnheader]`
              elements, and inside the lead gutter an ENABLED
              `button[role=checkbox]` whose accessible name was "Select all 0
              loaded rows" — a keyboard-reachable control naming a set of zero
              and toggling nothing. The product had two no-rows states and
              dressed them differently. The predicate here is now the same one
              the whole-list error panel itself uses (`hasError && !hasRows`,
              further down), so the two agree by construction. A FAILED APPEND
              is a different situation — there are live rows under it — and
              keeps its header. */}
          <div role="rowgroup" className="contents">
          {(showEmpty && !isFiltered) || (hasError && !hasRows) ? null : (
          <div
            ref={headRef}
            role="row"
            aria-rowindex={1}
            className="af-dt-head sticky top-0 z-[var(--z-sticky)] flex h-[var(--h-row-compact)] items-stretch border-b border-line"
          >
            <div
              {...(leadInert
                ? { role: 'presentation' as const }
                : { role: 'columnheader' as const, 'aria-colindex': 1 })}
              style={{ width: GUTTER }}
              className={cn(
                'af-dt-sticky sticky left-0 z-[1] flex shrink-0 items-center justify-end pr-2',
                !stickyFirstColumn && 'af-dt-sticky-edge',
              )}
            >
              {selectable ? (
                <span
                  onPointerDownCapture={captureShift}
                  onClick={(event) => event.stopPropagation()}
                  className="flex items-center"
                >
                  <Checkbox
                    // A roving stop in the header, not a tab stop: reached with
                    // ArrowUp out of row 1 then Home / ←, like every other
                    // header control on this grid.
                    tabIndex={-1}
                    data-header-select="true"
                    /* The head now survives a search that matched nothing (see
                       the header rule above), and "Select all 0 loaded rows" is
                       a control that names an empty set and toggles nothing.
                       The head keeps its columns; this one control goes dead
                       with the body it acts on. */
                    disabled={!hasRows}
                    checked={allLoadedSelected ? true : someLoadedSelected ? 'indeterminate' : false}
                    onCheckedChange={toggleAllLoaded}
                    /* "loaded", not "on screen". The control selects every row
                       the client holds — 50 at the first page — while a 700px
                       port at the shared pitch shows about eighteen of them, so
                       the old name stated a number a screen-reader user could
                       verify was false. "Loaded" is also the word the Copy
                       emails toast already uses for this same set ("The rows
                       loaded so far. Export sends the whole result set."), so
                       the two strings finally agree. */
                    aria-label={
                      allLoadedSelected
                        ? 'Clear selection'
                        : `Select all ${rows.length} loaded rows`
                    }
                  />
                </span>
              ) : null}
            </div>

            {headers.map((header, index) => (
              <HeaderCell<T>
                key={header.id}
                header={header}
                colIndex={index + colOffset}
                width={widthOf(header.column.id, header.getSize())}
                sortIndex={sortingState.findIndex((entry) => entry.id === header.column.id)}
                sortCount={sortingState.length}
                sticky={stickyFirstColumn && index === 0}
                dragState={dragState}
                resizingRef={resizingRef}
                onCycleSort={cycleSort}
                onSetSort={setSort}
                onSetDragState={setDragState}
                onMoveColumn={moveColumn}
                onShiftColumn={shiftColumn}
              />
            ))}

            <div
              {...(trailHeaderInert
                ? { role: 'presentation' as const }
                : {
                    role: 'columnheader' as const,
                    'aria-colindex': columnCount,
                    // What the column under it holds — the rail, or (on a grid
                    // that keeps its own picker) the column controls.
                    'aria-label': trailInert ? 'Column options' : 'Row actions',
                  })}
              style={{ minWidth: GUTTER }}
              className="flex flex-1 items-center justify-end"
            >
              {showColumnPicker ? (
                <span className="af-dt-sticky sticky right-0 flex items-center pr-3">
                  <ColumnPicker<T> columns={leafColumns} onReset={resetColumns} />
                </span>
              ) : null}
            </div>
          </div>
          )}
          </div>

          {/* ── selection escalation ───────────────────────────────────── */}
          {showEscalation ? (
            <div className="flex h-[var(--h-row-compact)] items-center border-b border-line-subtle bg-[var(--accent-soft)]">
              <span className="sticky left-0 flex items-center gap-2 px-3 text-sm text-secondary">
                {selection?.allMatching ? (
                  <>
                    <span>
                      All{' '}
                      <span className="font-medium text-primary tnum">
                        {totalMatching.toLocaleString('en-US')}
                      </span>{' '}
                      matching rows are selected.
                    </span>
                    <button
                      type="button"
                      className={escalationLinkClass}
                      onClick={() => selection?.onSelectAllMatching?.(false)}
                    >
                      Clear selection
                    </button>
                  </>
                ) : (
                  <>
                    {/* Same set, same word as the checkbox above it: these are
                        the rows the client has LOADED, not the ~18 a fold
                        shows. */}
                    <span>
                      All <span className="font-medium text-primary tnum">{data.length}</span>{' '}
                      loaded rows are selected.
                    </span>
                    <button
                      type="button"
                      className={escalationLinkClass}
                      onClick={() => selection?.onSelectAllMatching?.(true)}
                    >
                      Select all {totalMatching.toLocaleString('en-US')} matching
                    </button>
                  </>
                )}
              </span>
            </div>
          ) : null}

          {/* ── body ───────────────────────────────────────────────────── */}
          <div
            ref={bodyRef}
            role="rowgroup"
            className="relative"
            /* Collapsed only when the error replaced the WHOLE list. A failed
               append still has rows under it and they still have to be laid
               out; zeroing the container here was half of what threw the error
               panel a thousand pixels off screen. */
            style={{
              height: showSkeleton
                ? skeletonRowCount * rowHeight
                : hasError && !hasRows
                  ? 0
                  : totalSize,
            }}
          >
            {showSkeleton
              ? Array.from({ length: skeletonRowCount }, (_, index) => (
                  <SkeletonRow<T>
                    key={index}
                    top={index * rowHeight}
                    height={rowHeight}
                    headers={headers}
                    widthOf={widthOf}
                    stickyFirstColumn={stickyFirstColumn}
                    leadInert={leadInert}
                    index={index}
                  />
                ))
              : hasError && !hasRows
                ? null
                : virtualItems.map((item) => {
                    const row = rows[item.index];
                    if (!row) return null;
                    return (
                      <DataRow<T>
                        key={item.key}
                        row={row}
                        index={item.index}
                        top={item.start - scrollMargin}
                        height={rowHeight}
                        layoutKey={layoutKey}
                        columnCount={columnCount}
                        selectable={selectable}
                        clickable={rowsClickable}
                        selected={selectedSet?.has(row.id) ?? false}
                        active={activeRowId === row.id}
                        focused={focusedIndex === item.index}
                        stickyFirstColumn={stickyFirstColumn}
                        hasLeadRenderer={renderRowLead !== undefined}
                        showIndex={showRowIndex}
                        leadInert={leadInert}
                        trailInert={trailInert}
                        colOffset={colOffset}
                        widthOf={widthOf}
                        onClickRow={handleRowClick}
                        onDoubleClickRow={handleRowDoubleClick}
                        onToggleSelect={handleRowToggle}
                        onCaptureShift={captureShift}
                        renderActions={renderActions}
                        renderActionsLabel={renderActionsLabel}
                        renderSelectLabel={renderSelectLabel}
                        renderLead={renderLead}
                      />
                    );
                  })}

            {hasMore && !showSkeleton ? (
              <div ref={sentinelRef} aria-hidden className="absolute inset-x-0 bottom-0 h-px" />
            ) : null}
          </div>

          {/* ── a failed APPEND ────────────────────────────────────────── */}
          {/* A failed page 2 is not a failed list, and it cannot be drawn like
              one. The centred panel at the foot of this component sits OUTSIDE
              the virtualiser's sized container: with rows on screen the reader
              is a thousand pixels down a 319-row list and the panel lands a
              thousand pixels above the viewport — measured at y=-966 with the
              cursor request stubbed to 429 — while `EndOfList` is suppressed by
              the same error, so the list simply appeared to stop under a
              toolbar still reading "319 contacts". The other half of the same
              bug painted it OVER live rows.
              A failed append belongs immediately under the last live row, in
              the slot `EndOfList` occupies, at exactly one row's height so
              nothing shifts when it lands and the recovery is always inside the
              port. `scripts/invariants-check.mjs` stubs a 429 on every list
              surface and asserts the retry control's rect is in the viewport. */}
          {hasError && hasRows ? (
            <div
              role="alert"
              data-append-error="true"
              className={cn(
                'sticky left-0 flex h-[var(--h-row)] items-center justify-center',
                'gap-[var(--space-3)] border-t border-line-subtle px-3',
              )}
            >
              <span className="flex min-w-0 items-center gap-[var(--space-2)] text-xs text-secondary">
                <AlertTriangle
                  className="size-3.5 shrink-0 text-[var(--danger)]"
                  strokeWidth={1.75}
                  aria-hidden
                />
                <span className="truncate">{appendErrorMessage}</span>
              </span>
              {onRetry ? (
                <Button
                  /* `secondary`: a recovery is never gold — §"What gold means". */
                  variant="secondary"
                  size="sm"
                  iconLeft={<RotateCw className="size-3.5" aria-hidden />}
                  onClick={onRetry}
                >
                  Try again
                </Button>
              ) : null}
            </div>
          ) : null}

          {/* ── end of list ────────────────────────────────────────────── */}
          {/* One sentence, built in `EndOfList` and nowhere else — the same one
              the Tasks queue and Settings → Team print. */}
          {!hasMore && !showSkeleton && hasRows && !hasError ? (
            <EndOfList
              className="sticky left-0"
              total={totalMatching}
              noun={rowNoun?.one ?? label.toLowerCase().replace(/s$/, '')}
              nounPlural={rowNoun?.many ?? label.toLowerCase()}
            />
          ) : null}

        </div>

        {/* Empty and the WHOLE-LIST error sit outside the column-width wrapper so
            they stay centred in the viewport rather than in the (possibly much
            wider) scroll content. A failed append is a different situation and
            is drawn above, under the last live row. */}
        {hasError && !hasRows ? (
          <div className="sticky left-0 flex items-start justify-center py-6">
            {errorState ?? <ErrorState error={error} onRetry={onRetry} />}
          </div>
        ) : showEmpty ? (
          /* Anchored to the top of the region and centred across it — see
             `.af-dt-empty`. */
          <div
            /* The narrowed case keeps the column head above it, so it measures
               its offset from the head's bottom edge instead of the toolbar's. */
            data-under-head={isFiltered ? 'true' : undefined}
            className="af-dt-empty sticky left-0 grid content-start justify-center"
          >
            {isFiltered
              ? (filteredEmpty ?? <EmptyState variant="filtered" />)
              : (empty ?? <EmptyState variant="default" />)}
          </div>
        ) : null}
      </div>

      {/* ── summary rail ───────────────────────────────────────────────────
          Outside the scroll port, not sticky inside it: the port carries an
          end-of-list padding, and a sticky child pins above that padding rather
          than to the window edge, leaving a live row visible underneath. The
          inner track is translated by the scroll offset instead, so a total
          still sits under its own column when the table is scrolled sideways. */}
      {showFooter ? (
        <div
          aria-label={`${label} summary`}
          // The same inset the port carries, so a total stays under its column.
          style={{ paddingInline: inset }}
          className={cn(
            // `box-border` is explicit, not inherited: the rail must measure the
            // same 32px as `.af-dt-head`, hairline included, so the two bands
            // that bookend the table are the same height on the 4px grid.
            'af-dt-foot relative box-border shrink-0 overflow-hidden border-t border-line',
            'h-[var(--h-row-compact)]',
          )}
        >
          <div
            className="flex h-full items-stretch"
            style={{ minWidth: contentMinWidth, transform: 'translateX(var(--af-dt-x, 0px))' }}
          >
            <div style={{ width: GUTTER }} className="shrink-0" />
            {headers.map((header, index) => {
              const cellMeta = header.column.columnDef.meta;
              const cellAlign = cellMeta?.align ?? 'left';
              const value = footer?.cells?.[header.column.id];
              return (
                <div
                  key={header.id}
                  style={{ width: widthOf(header.column.id, header.getSize()) }}
                  className={cn(
                    'flex min-w-0 shrink-0 items-center px-2 text-sm text-muted tnum',
                    cellAlign === 'right'
                      ? 'justify-end text-right'
                      : cellAlign === 'center'
                        ? 'justify-center'
                        : '',
                  )}
                >
                  {index === 0 && footer?.note !== undefined ? (
                    // Deliberately unclipped: the note runs across the first few
                    // columns, which never carry a total.
                    <span className="whitespace-nowrap">{footer.note}</span>
                  ) : (
                    (value ?? null)
                  )}
                </div>
              );
            })}
            <div style={{ minWidth: GUTTER }} className="flex-1" />
          </div>
        </div>
      ) : null}

      {/* ── bottom loading bar ───────────────────────────────────────────── */}
      <div
        aria-hidden
        className={cn(
          'af-dt-loadbar pointer-events-none absolute inset-x-0 bottom-0 h-[2px]',
          'transition-opacity ease-[var(--ease-out)] duration-[var(--dur-fast)]',
          isLoadingMore || (loading && hasRows) ? 'opacity-100' : 'opacity-0',
        )}
      />
    </div>
  );
}

/* `--text-primary` + a `--line-strong` rule, not `--accent-text` + `--accent-line`.
   Escalating a selection is not a create, and the brand mark is spent on create
   alone; the link is marked as a link by its underline, which is what an
   underline is for. */
const escalationLinkClass = cn(
  'rounded-[var(--radius-sm)] px-1 font-medium text-primary',
  'underline decoration-[var(--line-strong)] underline-offset-2',
  'transition-colors ease-[var(--ease-out)] duration-[var(--dur-fast)] hover:text-primary hover:decoration-current',
  'focus-visible:outline-2 focus-visible:outline-offset-1 outline-[color:var(--focus)]',
);

/* ── header cell ──────────────────────────────────────────────────────────── */

/** One arrow press on a resize handle, and one press with Shift held. */
const RESIZE_STEP = 8;
const RESIZE_STEP_LARGE = 48;

interface DragState {
  id: string;
  over: string | null;
  after: boolean;
}

interface HeaderCellProps<T> {
  header: Header<T, unknown>;
  colIndex: number;
  /** The column's rendered width — its size plus any surplus it absorbs. */
  width: number;
  sortIndex: number;
  sortCount: number;
  sticky: boolean;
  dragState: DragState | null;
  resizingRef: RefObject<boolean>;
  onCycleSort: (columnId: string, additive: boolean) => void;
  onSetSort: (columnId: string, desc: boolean) => void;
  onSetDragState: (next: DragState | null) => void;
  onMoveColumn: (sourceId: string, targetId: string, after: boolean) => void;
  onShiftColumn: (columnId: string, delta: number) => void;
}

function HeaderCell<T>({
  header,
  colIndex,
  width,
  sortIndex,
  sortCount,
  sticky,
  dragState,
  resizingRef,
  onCycleSort,
  onSetSort,
  onSetDragState,
  onMoveColumn,
  onShiftColumn,
}: HeaderCellProps<T>) {
  const column = header.column;
  const meta = column.columnDef.meta;
  const canSort = column.getCanSort();
  const canResize = column.getCanResize() && meta?.resizable !== false;
  const align = meta?.align ?? 'left';
  const sorted = sortIndex >= 0;
  const descending = column.getIsSorted() === 'desc';
  const isDropTarget = dragState?.over === column.id;
  const label = columnLabel(column);

  const menuItems: MenuItem[] = [
    {
      id: 'asc',
      label: 'Sort ascending',
      icon: ArrowUp,
      disabled: !canSort,
      onSelect: () => onSetSort(column.id, false),
    },
    {
      id: 'desc',
      label: 'Sort descending',
      icon: ArrowDown,
      disabled: !canSort,
      onSelect: () => onSetSort(column.id, true),
    },
    { id: 'sep1', type: 'separator' },
    { id: 'left', label: 'Move left', onSelect: () => onShiftColumn(column.id, -1) },
    { id: 'right', label: 'Move right', onSelect: () => onShiftColumn(column.id, 1) },
    { id: 'sep2', type: 'separator' },
    {
      id: 'hide',
      label: 'Hide column',
      icon: EyeOff,
      /*
        A list ordered by a column it does not show is unreadable — the ordering
        has no visible key. So the sorted column cannot be hidden, and the
        control says why rather than vanishing. Same rule in the Columns picker
        below, in the inbox's record-pane suppression and in `RecordListShell`.
      */
      description: sorted ? SORT_LOCK_REASON : undefined,
      disabled: sorted || meta?.hideable === false || !column.getCanHide(),
      onSelect: () => column.toggleVisibility(false),
    },
  ];

  const headerOptions = (
    /* No padding of its own. The 26px box already carries 7px of air around a
       12px glyph, and the header button's own `--space-2` inset is the gap on
       the label side — a second 4px each way was 8px per column of nothing,
       taken out of the two flex columns' surplus eight times over.

       On a right-aligned head the wrapper carries `.af-dt-hopt-rail` and is
       lifted out of flow entirely — see the note beside that rule in GRID_CSS. */
    <span
      className={cn(
        'flex shrink-0 items-center self-center',
        align === 'right' && 'af-dt-hopt-rail',
      )}
    >
      <Menu
        items={menuItems}
        align="start"
        label={`${label} column options`}
        trigger={
          // No `Tooltip` wrapper here: it consumes the trigger props Radix
          // hands to `asChild`, so the menu never opened — by mouse or by
          // keyboard. The hint is a native `title` instead.
          <button
            type="button"
            tabIndex={-1}
            data-header-menu="true"
            title={`${label} options`}
            aria-label={`${label} column options`}
            className={cn(
              'af-dt-hopt flex size-[var(--h-control-sm)] shrink-0 items-center justify-center',
              'rounded-[var(--radius-xs)] text-muted opacity-0',
              'transition-[opacity,color,background-color] ease-[var(--ease-out)] duration-[var(--dur-fast)]',
              'hover:bg-[var(--surface-hover)] hover:text-primary',
              'group-hover/h:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100',
            )}
          >
            <ChevronDown className="size-3" aria-hidden />
          </button>
        }
      />
    </span>
  );

  return (
    <div
      role="columnheader"
      aria-colindex={colIndex}
      // aria-sort is gated on `canSort` in *every* branch, not only on `none`.
      // A column with a disabled control that still announces "sorted
      // descending" tells a screen-reader user the header is operable when its
      // only control cannot be reached or pressed.
      aria-sort={
        !canSort ? undefined : sorted ? (descending ? 'descending' : 'ascending') : 'none'
      }
      draggable
      data-dragging={dragState?.id === column.id ? 'true' : undefined}
      data-drop={isDropTarget ? (dragState?.after ? 'after' : 'before') : undefined}
      onDragStart={(event) => {
        if (resizingRef.current) {
          event.preventDefault();
          return;
        }
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/plain', column.id);
        onSetDragState({ id: column.id, over: null, after: false });
      }}
      onDragOver={(event) => {
        if (!dragState || dragState.id === column.id) return;
        event.preventDefault();
        const rect = event.currentTarget.getBoundingClientRect();
        const after = event.clientX > rect.left + rect.width / 2;
        if (dragState.over !== column.id || dragState.after !== after) {
          onSetDragState({ ...dragState, over: column.id, after });
        }
      }}
      onDrop={(event) => {
        event.preventDefault();
        if (dragState && dragState.id !== column.id) {
          onMoveColumn(dragState.id, column.id, dragState.after);
        }
        onSetDragState(null);
      }}
      onDragEnd={() => onSetDragState(null)}
      style={{ width, left: sticky ? GUTTER : undefined }}
      className={cn(
        'af-dt-hcell group/h relative flex shrink-0 items-stretch',
        // The trigger is an overlay on this head only — see `.af-dt-hcell-r`.
        align === 'right' && 'af-dt-hcell-r',
        sticky && 'af-dt-sticky af-dt-sticky-edge sticky z-[1]',
        meta?.headerClassName,
      )}
    >
      {/* The options trigger sits on the side the label does not hang off:
          outboard-left for a right-aligned column, outboard-right for every
          other. On a LEFT-aligned head it is in flow, because that column is
          sized by its widest cell rather than by its label — taking it out of
          flow there releases 0px and only re-opens the old defect, where an
          absolutely positioned chevron painted over the label at exactly the
          moment the pointer arrived (measured at 1440: SCORE lost 24px of a
          37px label, PRIORITY 7.2, CREATED 4).

          A RIGHT-aligned head is the opposite case and takes the overlay, with
          no width at rest and a matching inset on the label while it is shown —
          the whole argument, and the arithmetic, is on `.af-dt-hcell-r` in
          GRID_CSS. `scripts/inbox-grid-check.mjs` still asserts the two boxes
          never overlap, in either state. */}
      {align === 'right' ? headerOptions : null}

      <button
        type="button"
        disabled={!canSort}
        // Out of the tab ring on purpose: ten columns × two buttons is twenty
        // stops in front of the first row. The grid's roving arrow keys reach
        // the header instead (ArrowUp from row 1, ←/→ across columns).
        tabIndex={-1}
        data-header-sort="true"
        onClick={(event) => canSort && onCycleSort(column.id, event.shiftKey)}
        className={cn(
          'relative flex min-w-0 flex-1 items-center gap-1',
          // One inset value on every REGISTER edge — the one a value lines up
          // against. --space-2 on the cell (`px-2` in RowCell) and --space-2
          // here on the side the label is flush to: `pr` for a right-aligned
          // (numeric) head over its own column of digits, `pl` for every other.
          // The previous tracking compensation pulled the right-aligned labels
          // to a 6px inset against an 8px column of digits — a 2px wobble down
          // the right edge of the table.
          // The OPPOSITE side of a right-aligned head is not a register edge: it
          // faces the options chevron, which is already `--h-control-sm` of
          // reserve. Spending 8px of air there cost the two numeric heads the
          // room their type glyph needs, on the two narrowest columns in the
          // grid — so that side is flush and the register edge is untouched.
          align === 'right' ? 'pr-[var(--space-2)]' : 'px-[var(--space-2)]',
          // Right-aligned (numeric) columns keep the label flush right, arrow outboard.
          align === 'right'
            ? 'flex-row-reverse justify-start text-right'
            : align === 'center'
              ? 'justify-center text-center'
              : 'justify-start text-left',
          // THE column-header treatment: the shared `.eyebrow` utility, the same
          // one the product's hand-rolled grids use, so a column head is one
          // definition and not three. This used to be a local copy at 11px /
          // 1.54px tracking in `--text-muted`, which put two eyebrow scales
          // 400px apart on `/settings/team` alone.
          'eyebrow',
          // ArrowUp out of row 1 lands here, so it is a real focus stop, and the
          // ring must be right on the first frame. Two rules keep it that way:
          // the colour is constant rather than scoped to `:focus-visible`, and
          // the transition names its properties instead of using the colour
          // shorthand, whose list carries `outline-color`.
          'transition-[color,background-color] ease-[var(--ease-out)] duration-[var(--dur-fast)] outline-[color:var(--focus)]',
          // A full step below the data it labels: no header sits at
          // --text-primary, which is the Name column's ink. The sorted column is
          // marked by its accent arrow, not by a second ink value — the ramp is
          // one step, not two.
          'text-secondary!',
          canSort ? 'hover:text-primary!' : 'cursor-default',
        )}
      >
        {/* THE GLYPH IS OUTBOARD; THE LABEL KEEPS THE REGISTER EDGE.

            Icon and label are ONE child of the button, not two, so the pair
            stays together whichever way the button's row runs. Which side the
            glyph takes is decided by the column's own register edge — the edge
            its values line up against — and it always takes the other one:

              left-aligned  values start at the cell's `--space-2` inset, so the
                            LABEL starts there too and the mark follows the word;
              right-aligned values end at the cell's inset, the row is
                            `flex-row-reverse`, and the mark leads the word.

            In flow ahead of a left-aligned label the mark pushed it 16px right
            of the data beneath it — Title's label at x=479 over cells at 463,
            Email 846 over 830, Region 1132 over 1116 — while the two numeric
            columns, whose labels are flush to their own values, showed on the
            same header row exactly what the other seven were missing. Two
            alignment rules on one row of nine heads. This is the rule the
            options trigger a few lines up already follows: outboard-left for a
            right-aligned column, outboard-right for every other. */}
        <span className="flex min-w-0 items-center gap-[var(--space-1)]">
          <span className="truncate">
            {flexRender(column.columnDef.header, header.getContext())}
          </span>
        </span>
        {sorted ? (
          /* NOT `text-accent-text`.
             Gold has exactly two jobs in this product — the one action a
             surface exists to enable, and a control's "on" state — and this
             arrow is neither: it is a state marker on a column head. Painted
             `--accent-text` it made gold mean three things on one screen at
             once (create, on, and "sorted by this"), and it hid from
             `checkGold()` because that sweep only read `background-color`.
             The arrow's PRESENCE is the mark; it does not also need the hue.
             `scripts/invariants-check.mjs` now sweeps `color` as well.

             AND ON A RIGHT-ALIGNED HEAD IT IS OUT OF FLOW.

             A numeric column here is sized by its own header word plus the
             header's chrome, so an in-flow mark meant every one of them had to
             reserve 16px (a 12px glyph and its 4px gap) for a state it wears
             only while it is the active sort — width the column then held in
             all eleven other states. On `/companies` at 1440 that reserve was
             80px across the five measurement columns, on a grid where the
             widest ink in fifty rows is 24px and the typical ink is one digit.

             The mark is absolutely positioned in the head's OWN leading air
             instead — the side away from the register edge, which the note on
             the button's padding a few lines up already establishes is not an
             alignment edge. The label keeps its flush-right position and its
             full resting width, and the column stops paying for the arrow when
             it is not wearing one.

             A LEFT-aligned head is untouched: its mark follows the word, the
             word starts at the register edge, and the identity columns it
             labels have width to spare. */
          <span
            className={cn(
              'flex shrink-0 items-center gap-0.5 text-primary',
              align === 'right' &&
                'pointer-events-none absolute inset-y-0 left-0 justify-center',
            )}
          >
            {descending ? (
              <ArrowDown className="size-3" strokeWidth={2.5} aria-hidden />
            ) : (
              <ArrowUp className="size-3" strokeWidth={2.5} aria-hidden />
            )}
            {sortCount > 1 ? <span className="text-2xs tnum">{sortIndex + 1}</span> : null}
          </span>
        ) : null}
      </button>

      {align === 'right' ? null : headerOptions}

      {canResize ? (
        /*
          A real control, not a pointer target.

          Column resize is the in-product remedy for a column that truncates —
          on the Inbox it is the only one — and it shipped as an `aria-hidden`
          span with a pointer handler and nothing else: no role, no name, no
          key handler, so a keyboard-only reader could not widen SUBJECT at all.
          The file's own note on `headerStops` already named this class of bug
          ("its sort control, its options menu and its resize handle — reachable
          by mouse only") and fixed the first two.

          `role="separator"` with `aria-valuenow/min/max` is the ARIA pattern for
          a window splitter, which is exactly what this is; ←/→ step by
          `RESIZE_STEP`, Shift by `RESIZE_STEP_LARGE`, Home restores the
          column's default. It joins the header's roving ring (see
          `headerStops`) rather than the page's tab order, like every other
          control in this grid.
        */
        <span
          role="separator"
          aria-orientation="vertical"
          aria-label={`Resize ${label}`}
          // The hint the accessible name must not carry. ←/→ belong to the
          // separator while it holds focus (that is the splitter pattern), so
          // the way back onto the column walk has to be stated somewhere.
          title={`Resize ${label} — ← → to size, ↑ to go back`}
          // Clamped, because `width` is the RENDERED width — the column's size
          // plus whatever surplus a flex column absorbed — and a `valuenow`
          // outside its own min/max is an incoherent announcement.
          aria-valuenow={Math.round(
            Math.max(
              column.columnDef.minSize ?? 0,
              Math.min(column.columnDef.maxSize ?? width, width),
            ),
          )}
          aria-valuemin={column.columnDef.minSize ?? 0}
          aria-valuemax={column.columnDef.maxSize ?? 1000}
          tabIndex={-1}
          data-header-resize="true"
          className="af-dt-resize"
          data-resizing={column.getIsResizing() ? 'true' : undefined}
          onPointerDown={(event) => {
            resizingRef.current = true;
            header.getResizeHandler()(event);
          }}
          onDoubleClick={() => column.resetSize()}
          onKeyDown={(event) => {
            if (event.metaKey || event.ctrlKey || event.altKey) return;
            // The way out. ←/→ belong to the separator while it has focus, so
            // the handle owes the reader a key that puts them back on the
            // column walk — otherwise the ring stops moving at the first edge.
            // ArrowDown is untouched and still drops into the rows.
            if (event.key === 'ArrowUp' || event.key === 'Escape') {
              const control = event.currentTarget
                .closest('[role="columnheader"]')
                ?.querySelector<HTMLElement>('[data-header-sort], [data-header-menu]');
              if (control) {
                event.preventDefault();
                event.stopPropagation();
                control.focus();
              }
              return;
            }
            if (event.key === 'Home') {
              event.preventDefault();
              event.stopPropagation();
              column.resetSize();
              return;
            }
            const direction =
              event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
            if (direction === 0) return;
            // Stop the header's own ←/→, which would move focus to the next
            // column instead of sizing this one.
            event.preventDefault();
            event.stopPropagation();
            const step = event.shiftKey ? RESIZE_STEP_LARGE : RESIZE_STEP;
            const min = column.columnDef.minSize ?? 0;
            const max = column.columnDef.maxSize ?? Number.MAX_SAFE_INTEGER;
            const next = Math.max(min, Math.min(max, column.getSize() + direction * step));
            header.getContext().table.setColumnSizing((old) => ({ ...old, [column.id]: next }));
          }}
        />
      ) : null}
    </div>
  );
}

function columnLabel<T>(column: Column<T, unknown>): string {
  const meta = column.columnDef.meta;
  if (meta?.label) return meta.label;
  const header = column.columnDef.header;
  return typeof header === 'string' ? header : column.id;
}

/* ── column picker ────────────────────────────────────────────────────────── */

function ColumnPicker<T>({
  columns,
  onReset,
}: {
  columns: Column<T, unknown>[];
  onReset: () => void;
}) {
  const hideable = columns.filter(
    (column) => column.getCanHide() && column.columnDef.meta?.hideable !== false,
  );
  const hiddenCount = hideable.filter((column) => !column.getIsVisible()).length;
  /* The sort key stays on screen. See `SORT_LOCK_REASON`. */
  const lockedBySort = new Set(
    columns.filter((column) => column.getIsSorted() !== false).map((column) => column.id),
  );

  return (
    <Popover
      align="end"
      side="bottom"
      width={244}
      label="Columns"
      trigger={
        // No `Tooltip` wrapper, for the same reason the header's options button
        // has none: Radix hands `Popover.Trigger asChild` its props to the first
        // child, and a Tooltip consumes them, so the panel never opened — by
        // mouse or by keyboard. The hint is a native `title`; the accessible
        // name is the aria-label, which says the same words plus the count.
        <button
          type="button"
          title="Columns"
          aria-label={hiddenCount ? `Column options, ${hiddenCount} hidden` : 'Column options'}
          className={cn(
            'relative flex size-[var(--h-control-sm)] items-center justify-center',
            'rounded-[var(--radius-sm)] text-muted',
            'transition-[color,background-color] ease-[var(--ease-out)] duration-[var(--dur-fast)]',
            'outline-[color:var(--focus)] focus-visible:outline-2 focus-visible:outline-offset-1',
            'hover:bg-[var(--surface-hover)] hover:text-primary',
            'active:bg-[var(--surface-active)]',
            'data-[state=open]:bg-[var(--surface-hover)] data-[state=open]:text-primary',
          )}
        >
          <Columns3 className="size-4" aria-hidden />
          {hiddenCount > 0 ? (
            <span
              aria-hidden
              /* `--focus`, not `--accent`: "some columns are hidden" is a state
                 marker on a control, not a create. */
              className="absolute right-0.5 top-0.5 size-1.5 rounded-[var(--radius-full)] bg-[var(--focus)]"
            />
          ) : null}
        </button>
      }
    >
      <PopoverHeader>Columns</PopoverHeader>
      <div className="min-h-0 flex-1 overflow-y-auto p-1">
        {hideable.map((column) => {
          const visible = column.getIsVisible();
          const locked = lockedBySort.has(column.id);
          return (
            <button
              key={column.id}
              type="button"
              role="switch"
              aria-checked={visible}
              disabled={locked}
              title={locked ? SORT_LOCK_REASON : undefined}
              aria-description={locked ? SORT_LOCK_REASON : undefined}
              onClick={() => column.toggleVisibility(!visible)}
              className={cn(
                'flex h-[var(--h-control)] w-full items-center gap-2 rounded-[var(--radius-sm)] px-2',
                'text-left text-base transition-colors ease-[var(--ease-out)] duration-[var(--dur-fast)]',
                locked
                  ? 'cursor-not-allowed text-primary'
                  : 'hover:bg-[var(--surface-hover)] active:bg-[var(--surface-active)]',
                visible ? 'text-primary' : 'text-muted',
              )}
            >
              {visible ? (
                <Eye className="size-4 shrink-0 text-muted" aria-hidden />
              ) : (
                <EyeOff className="size-4 shrink-0 text-muted" aria-hidden />
              )}
              <span className="min-w-0 flex-1 truncate">{columnLabel(column)}</span>
              {/* The reason, on the row rather than only in a tooltip: the same
                  words the header menu prints under its disabled Hide item. */}
              {locked ? <span className="shrink-0 text-xs text-muted">{SORT_LOCK_TAG}</span> : null}
            </button>
          );
        })}
      </div>
      <PopoverFooter>
        <span className="text-sm text-muted tnum">
          {hideable.length - hiddenCount}/{hideable.length} shown
        </span>
        <button
          type="button"
          onClick={onReset}
          className={cn(
            'flex h-[var(--h-control-sm)] items-center gap-1.5 rounded-[var(--radius-sm)] px-2',
            'text-sm text-secondary transition-colors ease-[var(--ease-out)] duration-[var(--dur-fast)]',
            'hover:bg-[var(--surface-hover)] hover:text-primary',
          )}
        >
          <RotateCcw className="size-3.5" aria-hidden />
          Reset
        </button>
      </PopoverFooter>
    </Popover>
  );
}

/* ── rows ─────────────────────────────────────────────────────────────────── */

interface DataRowProps<T> {
  row: Row<T>;
  index: number;
  top: number;
  height: number;
  /** Changes whenever column geometry changes, so memoised rows re-measure. */
  layoutKey: string;
  columnCount: number;
  selectable: boolean;
  /** Adds the pointer cursor — a row that navigates has to read as a target. */
  clickable: boolean;
  selected: boolean;
  active: boolean;
  focused: boolean;
  stickyFirstColumn: boolean;
  hasLeadRenderer: boolean;
  /** Print the ordinal in the gutter when there is no lead renderer. */
  showIndex: boolean;
  /** The gutter holds nothing — it keeps its width but leaves the ARIA grid. */
  leadInert: boolean;
  /** Same, for the trailing gutter: no action rail and no Columns picker. */
  trailInert: boolean;
  /** `aria-colindex` of the first data column: 2 with a gutter cell, 1 without. */
  colOffset: number;
  widthOf: (columnId: string, size: number) => number;
  onClickRow: (index: number) => void;
  onDoubleClickRow: (index: number) => void;
  onToggleSelect: (index: number) => void;
  onCaptureShift: (event: ReactPointerEvent<HTMLElement>) => void;
  renderActions: (row: T) => ReactNode;
  renderActionsLabel: (row: T) => string;
  renderSelectLabel: (row: T, index: number) => string;
  renderLead: (row: T, index: number) => ReactNode;
}

function DataRowInner<T>({
  row,
  index,
  top,
  height,
  columnCount,
  selectable,
  clickable,
  selected,
  active,
  focused,
  stickyFirstColumn,
  hasLeadRenderer,
  showIndex,
  leadInert,
  trailInert,
  colOffset,
  widthOf,
  onClickRow,
  onDoubleClickRow,
  onToggleSelect,
  onCaptureShift,
  renderActions,
  renderActionsLabel,
  renderSelectLabel,
  renderLead,
}: DataRowProps<T>) {
  const style = useMemo<CSSProperties>(
    () => ({ transform: `translateY(${top}px)`, height }),
    [height, top],
  );

  const cells = row.getVisibleCells();
  const actions = renderActions(row.original);

  return (
    <div
      role="row"
      aria-rowindex={index + 2}
      aria-selected={selectable ? selected : undefined}
      data-row-index={index}
      data-selected={selected ? 'true' : undefined}
      data-active={active ? 'true' : undefined}
      tabIndex={focused ? 0 : -1}
      onClick={() => onClickRow(index)}
      onDoubleClick={() => onDoubleClickRow(index)}
      style={style}
      className={cn(
        'af-dt-row group/row absolute inset-x-0 top-0 flex items-stretch',
        // --line, not --line-subtle: at 1.15:1 the divider was invisible, and it
        // is the only guide across 1200px of columns.
        'border-b border-line outline-none',
        clickable && 'cursor-pointer',
      )}
    >
      <div
        {...(leadInert
          ? { role: 'presentation' as const }
          : { role: 'gridcell' as const, 'aria-colindex': 1, 'data-col-index': 1, tabIndex: -1 })}
        style={{ width: GUTTER }}
        className={cn(
          'af-dt-sticky sticky left-0 z-[1] flex shrink-0 items-center justify-end pr-2',
          !stickyFirstColumn && 'af-dt-sticky-edge',
        )}
      >
        {/* Lead glyph underneath, checkbox on top — they swap on hover without
            either of them moving. Right-aligned, so 1, 10 and 100 share an edge
            with the numeric columns instead of ragging by 3px either side.
            Nothing is drawn here at all when the gutter holds neither a lead
            renderer nor an ordinal, and then the checkbox below rests visible
            instead of waiting for a pointer. */}
        {hasLeadRenderer || showIndex ? (
          <span
            aria-hidden
            className={cn(
              'pointer-events-none absolute inset-0 flex items-center justify-end pr-2',
              // 11px, the row's small size — a third type size for a row number is
              // one more than a 40px band can carry.
              'text-xs text-muted tnum transition-opacity ease-[var(--ease-out)] duration-[var(--dur-fast)]',
              selectable &&
                (selected
                  ? 'opacity-0'
                  : 'group-hover/row:opacity-0 group-focus-within/row:opacity-0'),
            )}
          >
            {hasLeadRenderer ? renderLead(row.original, index) : index + 1}
          </span>
        ) : null}

        {selectable ? (
          <span
            onPointerDownCapture={onCaptureShift}
            onClick={(event) => event.stopPropagation()}
            onDoubleClick={(event) => event.stopPropagation()}
            className={cn(
              'relative flex items-center transition-opacity ease-[var(--ease-out)] duration-[var(--dur-fast)]',
              /* With nothing under it the box is the gutter's resting state, not
                 a thing the pointer reveals: an unchecked box at rest, filled on
                 select. A hover-only checkbox over an EMPTY 30px slot is an
                 affordance with no resting sign that it is there. */
              selected || (!hasLeadRenderer && !showIndex)
                ? 'opacity-100'
                : 'opacity-0 group-hover/row:opacity-100 group-focus-within/row:opacity-100',
            )}
          >
            <Checkbox
              // Fifty rows are fifty tab stops; the grid is one. Space on the
              // focused row toggles it, ← reaches the gutter cell, and the row's
              // `aria-selected` carries the state either way.
              tabIndex={-1}
              checked={selected}
              onCheckedChange={() => onToggleSelect(index)}
              aria-label={renderSelectLabel(row.original, index)}
            />
          </span>
        ) : null}
      </div>

      {cells.map((cell, cellIndex) => (
        <RowCell<T>
          key={cell.id}
          cell={cell}
          colIndex={cellIndex + colOffset}
          width={widthOf(cell.column.id, cell.column.getSize())}
          sticky={stickyFirstColumn && cellIndex === 0}
        />
      ))}

      <div
        {...(trailInert
          ? // Nothing lands here on this grid, so it is not a cell: an unnamed
            // `role=gridcell` at the end of every row is one empty announcement
            // per row and one ←/→ stop that contains nothing. See `trailInert`.
            { role: 'presentation' as const }
          : {
              role: 'gridcell' as const,
              'aria-colindex': columnCount,
              'data-col-index': columnCount,
              // The cell a reader arrives at with →, so it says what it is.
              // Unnamed, it announced as an empty cell that happened to open a
              // menu on Enter.
              'aria-label': actions ? renderActionsLabel(row.original) : undefined,
              tabIndex: -1,
            })}
        style={{ minWidth: GUTTER }}
        className="flex flex-1 items-center justify-end"
      >
        {actions ? (
          <span
            onClick={(event) => event.stopPropagation()}
            className={cn(
              /*
                The rail is exactly as wide as the gutter it lives in, and it is
                the tokens that say so: one `sm` control plus `--space-1` is
                `--h-control`, which is the gutter. At `px-2` it measured 12px
                wider and, being `shrink-0` and right-stuck, it hung those 12px
                over the last column — an opaque box painting out the final glyph
                of every right-aligned value on the hovered row ("2d" → "2ᴄ",
                "5mo" → "5mᴏ"). Now it sits beside the data instead of on it.
              */
              'af-dt-rail sticky right-0 flex h-full shrink-0 items-center gap-1',
              'pr-[var(--space-1)] pl-0',
              /*
                `opacity-0` plus `pointer-events-none`, NOT `visibility: hidden`.

                The hidden rail solved a real bug — an opacity-0 rail is still
                hit-testable, so every row carried a 28px invisible button
                sitting exactly where a pointer aimed at the last column's right
                edge overshoots — but it solved it with the one property that
                also removes the node from the ACCESSIBILITY tree. The row's only
                write affordance was therefore absent from it: the trailing cell
                exposed no name and the button's own `aria-label` never reached a
                screen reader, on every grid in the product. `pointer-events`
                takes the pointer half and leaves the semantics alone, which is
                how the pipeline table's row menu already did it.
              */
              'pointer-events-none opacity-0 transition-opacity ease-[var(--ease-out)] duration-[var(--dur-fast)]',
              'group-hover/row:pointer-events-auto group-hover/row:opacity-100',
              // An open row menu holds focus, so `focus-within` is what keeps the
              // rail up while the pointer wanders off it.
              'group-focus-within/row:pointer-events-auto group-focus-within/row:opacity-100',
            )}
          >
            {actions}
          </span>
        ) : null}
      </div>
    </div>
  );
}

const DataRow = memo(DataRowInner) as typeof DataRowInner;

interface RowCellProps<T> {
  cell: Cell<T, unknown>;
  colIndex: number;
  width: number;
  sticky: boolean;
}

function RowCellInner<T>({ cell, colIndex, width, sticky }: RowCellProps<T>) {
  const meta = cell.column.columnDef.meta;
  const align = meta?.align ?? 'left';
  return (
    <div
      role="gridcell"
      aria-colindex={colIndex}
      data-col-index={colIndex}
      tabIndex={-1}
      style={{ width, left: sticky ? GUTTER : undefined }}
      className={cn(
        'flex min-w-0 shrink-0 items-center px-2 text-base text-secondary',
        sticky && 'af-dt-sticky af-dt-sticky-edge sticky z-[1]',
        align === 'right'
          ? 'justify-end text-right tnum'
          : align === 'center'
            ? 'justify-center'
            : '',
        meta?.cellClassName,
      )}
    >
      {meta?.noTruncate ? (
        <span className="min-w-0">{flexRender(cell.column.columnDef.cell, cell.getContext())}</span>
      ) : (
        <span className="min-w-0 truncate">
          {flexRender(cell.column.columnDef.cell, cell.getContext())}
        </span>
      )}
    </div>
  );
}

const RowCell = memo(RowCellInner) as typeof RowCellInner;

/* ── skeleton ─────────────────────────────────────────────────────────────── */

const SKELETON_WIDTHS = ['62%', '44%', '78%', '35%', '56%', '70%', '48%', '66%'];

/** Rows fade toward the fold so the shimmer has a direction. */
function fade(index: number): number {
  return 1 - Math.min(index, 8) * 0.07;
}

function SkeletonRow<T>({
  top,
  height,
  headers,
  widthOf,
  stickyFirstColumn,
  leadInert,
  index,
}: {
  top: number;
  height: number;
  headers: Header<T, unknown>[];
  widthOf: (columnId: string, size: number) => number;
  stickyFirstColumn: boolean;
  leadInert: boolean;
  index: number;
}) {
  return (
    <div
      role="row"
      aria-hidden
      style={{ transform: `translateY(${top}px)`, height }}
      className="af-dt-row absolute inset-x-0 top-0 flex items-stretch border-b border-line"
    >
      <div
        style={{ width: GUTTER }}
        className={cn(
          'af-dt-sticky sticky left-0 z-[1] flex shrink-0 items-center justify-end pr-2',
          !stickyFirstColumn && 'af-dt-sticky-edge',
        )}
      >
        {/* Nothing lands in an inert gutter, so nothing shimmers there either. */}
        {leadInert ? null : (
          <span className="skeleton block h-2 w-3" style={{ opacity: fade(index) }} />
        )}
      </div>
      {headers.map((header, cellIndex) => {
        const meta = header.column.columnDef.meta;
        const lead = meta?.skeletonLead;
        return (
          <div
            key={header.id}
            style={{
              width: widthOf(header.column.id, header.getSize()),
              left: stickyFirstColumn && cellIndex === 0 ? GUTTER : undefined,
            }}
            className={cn(
              'flex shrink-0 items-center gap-2 px-2',
              (meta?.align ?? 'left') === 'right' && 'justify-end',
              stickyFirstColumn && cellIndex === 0 && 'af-dt-sticky af-dt-sticky-edge sticky z-[1]',
            )}
          >
            {/* The lead glyph is part of the geometry: without it the avatar
                pops in from nothing the moment the rows land. */}
            {lead ? (
              <span
                className={cn(
                  'skeleton block shrink-0',
                  // Exactly the glyphs these stand in for, so nothing resizes
                  // when the data lands: `Avatar` sm (5.5 units), `Avatar` xs
                  // (18px — a record mark in a reference cell) and `Monogram`
                  // xs (5 units).
                  lead === 'avatar'
                    ? 'size-5.5 rounded-[var(--radius-full)]'
                    : lead === 'avatar-xs'
                      ? 'size-[18px] rounded-[var(--radius-full)]'
                      : 'size-5 rounded-[var(--radius-xs)]',
                )}
                style={{ opacity: fade(index) }}
              />
            ) : null}
            {/* A record-reference column lands as a bordered chip, so its
                shimmer is a bordered chip too. With a flat 8px bar here, 31
                outlined boxes appeared out of nothing at the instant the data
                arrived — the one place the skeleton stopped predicting the
                final screen, on a grid whose geometry is otherwise exact. */}
            {meta?.skeletonShape === 'chip' ? (
              <span
                className={cn(
                  'flex h-[var(--h-control-sm)] items-center',
                  'rounded-[var(--radius-sm)] border border-line-object px-[var(--space-1)]',
                )}
                style={{
                  width: SKELETON_WIDTHS[(cellIndex + index) % SKELETON_WIDTHS.length],
                  opacity: fade(index),
                }}
              >
                <span className="skeleton block h-2 w-full" />
              </span>
            ) : (
              <span
                className="skeleton block h-2"
                style={{
                  width: SKELETON_WIDTHS[(cellIndex + index) % SKELETON_WIDTHS.length],
                  opacity: fade(index),
                }}
              />
            )}
          </div>
        );
      })}
      <div style={{ minWidth: GUTTER }} className="flex-1" />
    </div>
  );
}

/* ── helpers ──────────────────────────────────────────────────────────────── */

function columnIdOf<T>(column: DataTableColumn<T>): string | undefined {
  if ('id' in column && typeof column.id === 'string') return column.id;
  if ('accessorKey' in column && typeof column.accessorKey === 'string') return column.accessorKey;
  return undefined;
}
